/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing stream processing gates feature behavior from runtime flags. */
import type { Action } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { Metric } from '@atlassian/browser-metrics/types';
import {
	getAIFCContentType,
	isCreatableAction,
	isUpdatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	customActionUpdater,
	getAccumulatedActionTypeGuard,
	getDynamicUiType,
	getPrimaryAccumulatedValue,
	isAccumulatedAction,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	remixEditExistingExperience,
	sendMessageExperienceName,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type CitedProductList,
	type SourceProducts,
	type TrackAIEventPayloads,
} from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { SkillSelectionSource } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import type { AdditionalAnalyticAttributes } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type AlphaExtendedThinkingMode,
	type Agent,
	type FetchStreamReturn,
	type FileUploadResponseItem,
	isChannelNotFoundError,
	isConversationChannelDataMessage,
	isScopeDeterminedMessage,
	isFollowUpMessage,
	isSSEMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAnswerGenerationType,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamDynamicLayoutSubAgent,
	isStreamError,
	isStreamFastModeEnabled,
	isStreamFastModeResponseAvailable,
	isStreamRemoveAnswerNow,
	isStreamMemoryUpdate,
	isStreamAttachmentBlock,
	isStreamPromptBlock,
	isStreamPromptRedaction,
	isStreamResponseBlock,
	isStreamResponseRedaction,
	isStreamReasoningPart,
	isStreamReconnectSupportPart,
	isStreamResearchPayload,
	isStreamResearchPlan,
	isStreamResearchStart,
	isStreamResponse,
	isStreamSkill,
	isStreamTrace,
	isStreamThinkDeeperTriggered,
	isStreamTaskStatus,
	isStreamTodoSnapshot,
	type StreamTaskStatus,
	type TodoItem,
	type AgentVersionInput,
	type LastAgentMessageStatus,
	type MessageFileId,
	type MessageType,
	type ParsedSSEMessage,
	type StreamMessage,
	type StreamResponse,
	isStreamAgenticUi,
	type StreamAgenticUi,
	type SendMessageSearchArtifact,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	type Store as ChatContextStore,
	type ContentContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	createProvisionalConversationAgent,
	resolveConversationAgentIdForRouting,
} from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import { deriveAgentSendRequestIdentity } from '@atlassian/conversation-assistant-store/controllers/chat/send-request-agent-identity';
import {
	type AgentMetadata,
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isStoreAssistant,
	isStoreHuman,
	type StoreAssistant,
	type StoreChatItem,
	type StoreGuardBlock,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { abortGenerateContentChildExperiences } from '../../common/utils/aifc-experience-collect/abort-generate-content-child-experiences';
import { createAifcCreateInChatExperienceCollect } from '../../common/utils/aifc-experience-collect/create-aifc-create-in-chat-experience-collect';
import { startUpdateActionExperiences } from '../../common/utils/aifc-experience-collect/start-update-action-experiences';
import { messageLatencyMetrics } from '../../common/utils/browser-metrics';
import {
	type ContentFeatures,
	extractContentFeatures,
	getTopLevelNodeTypes,
	countFragmentWords,
} from '../../common/utils/content-features';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import { DEFAULT_AGENT } from '../agents/constants';
import type { PromptStreamHook } from '../bridge-api/handlers/use-prompt-stream';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle';
import { setLastMessageSentAt } from '../last-message-sent';

import { createDeadAirTracker } from './dead-air-tracker';
import {
	DeepResearchError,
	NoFinalResponseError,
	StreamDisconnectedError,
	StreamErrorException,
	StreamGeneratorError,
} from './error-classes';
import { getPageContext } from './get-page-context';
import { getViewContext } from './get-view-context';
import { buildUnifiedStreamTaskTarget, handleStreamTaskStatus } from './handle-stream-task-status';
import {
	getInteractionModeExecutedFromStreamResponse,
	getInteractionModeRequested,
	interactionModeExecutedAttributes,
	interactionModeRequestedAttributes,
} from './interaction-mode';
import { minionVerificationAnalytics } from './minion-verification-analytics';
import { type ReconnectCompletionResult } from './reconnect-action';
import { createChatReconnectableStream } from './reconnectable-stream';
import { createStoreAbortController } from './store-abort-controller';
import { recordStreamMessage, resetStreamResponseRecording } from './stream-response-recorder';
import type {
	ChatStoreAction,
	ChatStoreContainerProps,
	InactivityWatchdogConfig,
	MessageSource,
	ResearchStreamState,
} from './types';
import {
	addActionPart,
	addAnswerPart,
	addAnswerPartWithMarkdownData,
	addAnswerPartWithMarkdownDataMap,
	addRequestIdToLatestHumanMessage,
	addSubAgentMessageToStoreAssistantMessage,
	clearStatus,
	clearStoreAssistantMessageContent,
	clearTracesIfNotEmpty,
	getStoreAssistantMessageContent,
	createBroadcastStreamLifecycle,
	createStreamMarkdownProcessor,
	createStreamMarkdownMultiTagProcessor,
	dedupeTraceMessagesWithLatest,
	findLastAssistantAction,
	generateId,
	generateTmpMessageId,
	getConversation,
	getConversationTraces,
	appendCreatePageFollowUpIfNeeded,
	createSyntheticCreatePageFollowUp,
	shouldAppendCreatePageFollowUp,
	getLastAgentMessageID,
	getSourceAgentId,
	getTraceFromStreamMessage,
	handleStagingAreaActionValidation,
	isClientSideStreamingError,
	isEmptyConversation,
	markStreamingMessagesAsComplete,
	processStreamResponseMarkdown,
	replaceAnswerData,
	shouldPreprocessDetailsForConversation,
	updateHumanMessageId,
	updateStoreAssistantMessageAnswerGenerationType,
	updateStoreAssistantMessageAnswerNowStatus,
	clearAnswerNowStatusForMessageIfSet,
	updateStoreAssistantMessageId,
	addMemorytoLatestAssistantMessage,
	type SkillAttributes,
	getActionPartKeyForMetrics,
	findStoreMessageIndex,
	getInvokedFrom,
	addAgenticUiMessage,
	extractStreamMeta,
} from './utils';

/**
 * Helper function to stop/end metrics, that can only be called once
 */
const getRequestedModeAttributes = (trackingAttributes: SendMessageTrackingAttributes) => ({
	...interactionModeRequestedAttributes(trackingAttributes.interactionModeRequested),
	...(trackingAttributes.aiFeatureName && { aiFeatureName: trackingAttributes.aiFeatureName }),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMetricEndHelper = (metrics: any) => {
	let called = false;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return (result: 'cancel' | 'stop', customData?: any): void => {
		if (!called) {
			if (result === 'stop') {
				metrics.stop({ customData });
			} else {
				metrics.cancel();
			}
			called = true;
		}
	};
};

/**
 * Returns the given source-control value unless this send is actually routed by
 * a selected-agent override. Selected agents bring their own fixed source set,
 * but stale/whitespace selected-agent drafts and cross-surface handoffs that do
 * not use the draft must preserve the caller's source controls.
 * Exported for unit testing.
 */
export function getSourceControlValueForBackend<T>({
	routedSelectedAgentId,
	value,
}: {
	/** Chat input selected-agent draft that actually routed this send. */
	routedSelectedAgentId?: string;
	value: T;
}): T | undefined {
	return routedSelectedAgentId ? undefined : value;
}

export type SendMessageAgentParam =
	| {
			agentId?: undefined;
			agent?: Agent;
	  }
	| {
			agentId: string;
			agent?: undefined;
	  };

export type ChatStoreActionsForSendMessage = {
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	createConversationOptimistically: (options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
		selectedAgentIds?: string[];
	}) => ChatStoreAction;
	addHumanMessage: (args_0: {
		id: string;
		content: DocNode;
		conversationId?: string;
		isFromSeededConversation?: boolean;
		requestId?: string;
		fileUploads?: MessageFileId[];
	}) => ChatStoreAction;
	addAssistantMessage: (args_0: {
		id: string;
		content: string | DocNode;
		conversationId?: string;
		agentNamedId?: string;
	}) => ChatStoreAction;
	clearFollowUpMessages: (args_0: { conversationId?: string }) => ChatStoreAction;
	setSelectedConversation: (args_0: {
		conversationId: string;
		sourceAgentId?: string;
	}) => ChatStoreAction;
	reconnectToInProgressStream: (args_0: {
		conversationId: string;
		messageId: string;
		answerIndex: number;
		lastEventId?: string;
		isDeepResearch?: boolean;
		isTask?: boolean;
		abortCurrentRequest?: boolean;
		onComplete?: (result: ReconnectCompletionResult) => void;
	}) => ChatStoreAction;
};
export type SendMessageConsumerPramas = {
	prompt: DocNode;
	conversationId?: string;
	storeUserMessages: boolean;
	messageSources?: Partial<MessageSource>;
	files?: FileUploadResponseItem[];
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	webSearchEnabled?: boolean;
	appFilters?: RovoApp[];
	addCurrentPageAsContextUserPreference?: boolean;
	productKey?: string;
	source?: string;
	promptStreamHook?: PromptStreamHook;
	creationContextParams?: CreationContextParams;
	additionalAnalyticAttributes?: AdditionalAnalyticAttributes;
	contentContext?: ContentContext;
	minionAlias?: string;
	onTempMessageIdReplacedOverride?: (oldId: string, newId: string) => void;
	openChat?: boolean;
	openChatOnError?: boolean;
	contentId?: string | number;
	agentVersion?: AgentVersionInput;
	spaceId?: string;
	/**
	 * Where the skill was selected from. Only relevant when a skill is explicitly invoked.
	 * Used to populate `skillSelectionSource` on explicit entries in `skillsInvoked`.
	 */
	skillSelectionSource?: SkillSelectionSource;
	/**
	 * When true, editorContext will not be fetched or sent in the request payload.
	 */
	skipEditorContext?: boolean;
	/**
	 * Whether the context banner (page context or space context) was visible when the
	 * message was submitted. When true, the backend should extract conversation scopes.
	 */
	shouldExtractConversationScopes?: boolean;
	/**
	 * Optional artifact representing the object the user is currently interacting with or
	 * acting on (e.g. 3P forge artifacts that are not resolvable without an ARI).
	 * Sent as `search_artifact` in `body.context`.
	 */
	searchArtifact?: SendMessageSearchArtifact;
	/**
	 * Mark this send as an edit of the user message whose parent in the
	 * conversation tree equals this id. Sent as `parent_message_id`.
	 */
	parentMessageId?: string;
	/**
	 * Edit-mode rollback. When set, on terminal send failure we remove the optimistic
	 * human + assistant for this turn and re-append `restoreMessages` to the conversation.
	 */
	editRollback?: {
		restoreMessages: StoreChatItem[];
	};
} & SendMessageAgentParam;

export type SendMessageParams = SendMessageConsumerPramas & ChatStoreActionsForSendMessage;

type SendMessageTrackingAttributes = {
	pluginsInvoked: Set<string>;
	skillsInvoked: SkillAttributes[];
	/** skillsInvoked is nested too deeply for Statsig, use executedStratusSkill to determine if at least one stratus skill has ran */
	executedStratusSkill: boolean;
	hasSources: boolean;
	messageId: string;
	messageSentTimeStamp: string;
	humanMessageId?: string;
	requestId?: string;
	messageActions?: string[];
	messageDynamicUIData?: {
		subtype?: string;
	}[];
	invokedFrom?: string;
	instrumentationId?: string;
	citedProductList: CitedProductList;
	sourceProducts: SourceProducts;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	webSearchEnabled?: boolean;
	interactionSource?: InteractionSource;
	followUpSource?: FollowUpSource;
	aiFeatureName?: string;
	contextProvided?: Record<keyof ChatContextStore, boolean> & { domContext?: boolean };
	sources?: string[];
	usesJsm?: boolean;
	isFirstPrompt: boolean;
	promptFeatures?: ContentFeatures;
	fileCount: number;
	appFilters: RovoApp[];
	appFilterCount: number;
	template: {
		templateId?: string;
		templateType?: string;
	};
	deepResearchInvoked: boolean;
	scenarioId?: string;
	pageContext?: string;
	viewContext?: Record<string, boolean | number | string>;
	forcedContentType?: string;
	interactionModeRequested: string;
	interactionModeExecuted?: string;
	remixNodeTypes?: string[];
	remixWordCount?: number;
};

const getInitialTrackingAttributes: (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: {
		templateId?: string;
		templateType?: string;
	},
	forcedContentType: string | undefined,
) => SendMessageTrackingAttributes = (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: { templateId?: string; templateType?: string },
	forcedContentType: string | undefined,
) => {
	return {
		pluginsInvoked: new Set(),
		skillsInvoked: [],
		executedStratusSkill: false,
		hasSources: false,
		messageId: '',
		humanMessageId: '',
		requestId: undefined,
		messageActions: [],
		messageDynamicUIData: [],
		invokedFrom: undefined,
		instrumentationId,
		messageSentTimeStamp: new Date().toISOString(),
		citedProductList: {},
		sourceProducts: {},
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		interactionSource: messageSources?.interactionSource,
		followUpSource: messageSources?.followUpSource ?? 'none',
		aiFeatureName: messageSources?.aiFeatureName,
		isFirstPrompt,
		promptFeatures: extractContentFeatures(prompt),
		fileCount: files?.length ?? 0,
		appFilters: appFilters ?? [],
		appFilterCount: appFilters?.length ?? 0,
		template,
		deepResearchInvoked: false,
		forcedContentType,
		interactionModeRequested: getInteractionModeRequested({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			taskModeEnabled,
		}),
	};
};

const getOptimisticMessageTypes = (
	deepResearchEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
) => {
	const messageType: MessageType[] = [];

	if (fg('rovo_chat_chat_history_refresh')) {
		if (deepResearchEnabled) {
			messageType.push('DEEP_RESEARCH');
		}
		if (webSearchEnabled) {
			messageType.push('WEB_SEARCH');
		}
	}

	return { messageType };
};

const initLatencyMetrics = ({
	deepResearchEnabled,
	thinkDeeperEnabled,
	fastModeEnabled,
	agentId,
}: {
	deepResearchEnabled: boolean | undefined;
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	agentId: string | undefined;
}) => {
	const rovoCustomAgent = isRovoCustomAgent(agentId);
	const sendMessageLatencyMetrics = messageLatencyMetrics.createSendMessageMetrics({
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		isRovoCustomAgent: rovoCustomAgent,
	});
	const timeToFirstAnswerPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToFirstActionPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstActionPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToActionCompletionLatencyMetrics =
		messageLatencyMetrics.createTimeToActionCompletionMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});

	const metricsStartTime = window.performance.now();
	sendMessageLatencyMetrics.start({ startTime: metricsStartTime });
	timeToFirstAnswerPartLatencyMetrics.start({ startTime: metricsStartTime });

	if (fg('aifc_create_enabled') || expValEquals('cc-maui-experiment', 'isEnabled', true)) {
		timeToFirstActionPartLatencyMetrics.start();
		timeToActionCompletionLatencyMetrics.start();
	}

	const stopInteractionMetricsTimeToFirstAnswerPart = getMetricEndHelper(
		timeToFirstAnswerPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToFirstActionPart = getMetricEndHelper(
		timeToFirstActionPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToActionCompletion = getMetricEndHelper(
		timeToActionCompletionLatencyMetrics,
	);

	const cancelStreamingMetrics = () => {
		stopInteractionMetricsTimeToFirstAnswerPart('cancel');
		stopInteractionMetricsTimeToFirstActionPart('cancel');
		stopInteractionMetricsTimeToActionCompletion('cancel');
	};

	return {
		sendMessageLatencyMetrics,
		timeToFirstAnswerPartLatencyMetrics,
		timeToFirstActionPartLatencyMetrics,
		timeToActionCompletionLatencyMetrics,
		stopInteractionMetricsTimeToFirstAnswerPart,
		stopInteractionMetricsTimeToFirstActionPart,
		stopInteractionMetricsTimeToActionCompletion,
		cancelStreamingMetrics,
		metricsStartTime,
	};
};

const hydrateStoreWithNewMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dispatch: <T extends Action<ChatStorePrivateState, any, any>>(actionThunk: T) => ReturnType<T>,
	conversationId: string,
	messageType: MessageType[],
	tmpHumanMessageId: string,
	tmpAssistantMessageId: string,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	sourceAgentIdForTemporaryLocalConversation: string,
	setConversationData: ChatStoreActionsForSendMessage['setConversationData'],
	addHumanMessage: ChatStoreActionsForSendMessage['addHumanMessage'],
	addAssistantMessage: ChatStoreActionsForSendMessage['addAssistantMessage'],
	clearFollowUpMessages: ChatStoreActionsForSendMessage['clearFollowUpMessages'],
	// The per-turn selected/@-mention override that actually routed this send, when
	// one exists (`deriveAgentSendRequestIdentity().routedSelectedAgentId`). This is
	// deliberately NOT `routingAgentId` (which falls back to the conversation agent):
	// the optimistic author must honour the caller's per-send agent intent (e.g. a
	// cross-surface default-Rovo "ask Rovo" into a specialist conversation, which must
	// stay default Rovo) and only switch to a genuine per-turn override here.
	routedSelectedAgentId?: string,
) => {
	const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

	const updateCurrentConversation = (data: Partial<Conversation>) => {
		dispatch(
			setConversationData({
				conversationId,
				data,
			}),
		);
	};

	// Author the optimistic assistant by per-turn override (`routedSelectedAgentId`),
	// else caller intent (`sourceAgentIdForTemporaryLocalConversation` = explicit/default
	// agent) — never the routing fallback, which would resolve to the conversation agent
	// and mis-author a cross-surface default-Rovo "ask Rovo". See ai-mate AGENTS.md
	// "Rovo Chat agent identity, routing, and display". Gate-off keeps caller intent;
	// on flag cleanup keep the `routedSelectedAgentId ?? sourceAgentId...` form.
	const normalizedRoutedSelectedAgentId = routedSelectedAgentId || undefined;
	const optimisticAssistantAuthorNamedId =
		fg('rovo_chat_agent_selection') && normalizedRoutedSelectedAgentId
			? normalizedRoutedSelectedAgentId
			: sourceAgentIdForTemporaryLocalConversation;

	updateCurrentConversation({
		status: CHAT_STORE_STATUS.awaitingResponse,
		storeAbortController,
		lastMessageType: messageType,
		lastAgentMessageStatus: 'IN_PROGRESS',
	});
	dispatch(
		addHumanMessage({
			id: tmpHumanMessageId,
			content: prompt,
			conversationId,
			fileUploads: files,
		}),
	);
	dispatch(
		addAssistantMessage({
			id: tmpAssistantMessageId,
			content: '',
			conversationId,
			// Authored by the routed agent (see derivation above) so the per-turn
			// "{agent} is thinking" indicator reflects it.
			agentNamedId: optimisticAssistantAuthorNamedId,
		}),
	);
	dispatch(clearFollowUpMessages({ conversationId }));

	return { abortController, storeAbortController, confirmAbort, updateCurrentConversation };
};

type HandleEditorContextAnalyticsParams = {
	source: string | undefined;
	contentContext: ContentContext;
	isFirstPrompt: boolean;
	userPreference: boolean;
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	activeTargetId?: boolean;
	isStagingArea?: boolean;
};

const handleEditorContextAnalytics = ({
	source,
	contentContext,
	isFirstPrompt,
	userPreference,
	sendAnalyticsEvent,
	activeTargetId,
	isStagingArea: stagingAreaFromStore,
}: HandleEditorContextAnalyticsParams) => {
	// If AIFC is not enabled, we should not send analytics
	if (!fg('aifc_create_enabled')) {
		return;
	}

	const inStagingArea = contentContext === 'staging-area';

	// Source values for Inline Rovo
	const sourceEligible =
		source === 'CONFLUENCE_INLINE_EDITOR' ||
		source === 'INLINE_EDITOR' ||
		source === 'CONFLUENCE_WHITEBOARD_TO_PAGE_BUTTON' ||
		source === 'CONFLUENCE_WHITEBOARD_BRAINSTORMING_FOLLOW_UP';

	const shouldProvideContext =
		// Inline Rovo
		sourceEligible ||
		// Within staging area & not the first prompt
		(inStagingArea && !isFirstPrompt) ||
		// Not within staging area, default to user preference
		(!inStagingArea && !!userPreference);

	// If there's no editor context when we should be providing it,
	// something went wrong so send analytics to track
	if (shouldProvideContext) {
		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'missing',
			actionSubject: 'editorContext',
			attributes: {
				contentContext,
				source,
				userPreference,
				isFirstPrompt,
				activeTargetId,
				isStagingArea: stagingAreaFromStore,
			},
		});
	}
};

type ChatContextArgs = {
	editorContext: unknown;
	whiteboardContext: unknown;
	databaseContext: unknown;
	slidesContext: unknown;
};

export function getChatContextContentType({
	editorContext,
	whiteboardContext,
	databaseContext,
	slidesContext,
}: ChatContextArgs): 'page' | 'whiteboard' | 'database' | 'slide' | undefined {
	if (editorContext) return 'page';
	if (whiteboardContext) return 'whiteboard';
	if (databaseContext) return 'database';
	if (slidesContext) return 'slide';
	return undefined;
}

export function getChatContextExperience({
	contentContextValue,
	existingExperience,
	chatContextContentType,
	isViewMode,
}: {
	contentContextValue: string;
	existingExperience: string | undefined;
	chatContextContentType: string | undefined;
	isViewMode: boolean;
}): { experience: string; contentTypes: string[] } | undefined {
	if (contentContextValue === 'global' && !existingExperience && chatContextContentType) {
		return {
			experience: isViewMode ? 'chat_view' : 'chat_edit',
			contentTypes: [chatContextContentType],
		};
	}
	return undefined;
}

const populateSendMessageContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	source: string | undefined,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	creationContextParams: CreationContextParams | undefined,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	activeTargetId?: string | number,
	lastAssistantMessageDynamicUiType?: string,
	skipEditorContext?: boolean,
	isRemixAction?: boolean,
	searchArtifact?: SendMessageSearchArtifact,
) => {
	const editorContext = skipEditorContext
		? undefined
		: await chatContextStoreActions.getEditorContext({
				isFirstPrompt,
				source,
				contentContext: contentContextValue,
				addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
				activeTargetId,
			});

	const currentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);

	// If there's no editor context available and the current target CAN
	// provide context, send analytics to track this error state
	if (!editorContext && !skipEditorContext && currentTarget?.canProvideContext) {
		handleEditorContextAnalytics({
			isFirstPrompt,
			source,
			contentContext: contentContextValue,
			userPreference: !!addCurrentPageAsContextUserPreference,
			sendAnalyticsEvent,
			activeTargetId: !!activeTargetId,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
		});
	}

	const whiteboardContext = await chatContextStoreActions.getWhiteboardContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const slidesContext = await chatContextStoreActions.getSlidesContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const databaseContext = await chatContextStoreActions.getDatabaseContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const dashboardEditorContext = await chatContextStoreActions.getDashboardEditorContext({
		contentContext: contentContextValue,
		activeTargetId,
	});

	const browserContext = await chatContextStoreActions.getBrowserContext({
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		enabled: FeatureGates.getExperimentValue(
			'rovo_extension_dom_improvement',
			'rovo_extension_dom_context_improvement_enabled',
			false,
			{
				fireExperimentExposure: false,
			},
		),
	});

	const jiraContext = await (async () => {
		// Only fetch Jira context when in staging area and product is Jira
		// and not the first prompt (refinement messages)
		if (
			!isFirstPrompt &&
			(contentContextValue === 'staging-area' || fg('jira-aifc-enable-preview-card-in-chat-v2')) &&
			productKey === 'jira' &&
			fg('jira_aifc_enabled')
		) {
			return chatContextStoreActions.getJiraContext?.();
		}
		return undefined;
	})();

	const additionalCreationContext = editorContext?.additionalContext;
	const additionalCreationContextParams = creationContextParams?.additionalContext;
	if (editorContext && editorContext.additionalContext) {
		delete editorContext.additionalContext;
	}
	const useGenericEditorSkill = editorContext?.useGenericEditorSkill;
	if (editorContext && 'useGenericEditorSkill' in editorContext) {
		delete editorContext.useGenericEditorSkill;
	}

	const isViewMode =
		contentContextValue === 'staging-area'
			? !!editorContext?.isViewMode
			: !!(
					editorContext?.isViewMode ||
					whiteboardContext?.isViewMode ||
					slidesContext?.isViewMode ||
					databaseContext?.isViewMode
				);
	const creationContextIsViewMode = expValEquals('cc-maui-experiment', 'isEnabled', true)
		? creationContextParams?.isViewMode !== undefined
			? creationContextParams.isViewMode
			: isViewMode
		: isViewMode;
	const isDraftLockedForEditing = !!editorContext?.isDraftLockedForEditing;
	// CWR follow-up/edit prompts in the staging area.
	// isRemixAction should be all that's needed to check remix.
	// source and creationContextParams.source checks included as future-proofing measures
	const isCwrEditFollowUp =
		contentContextValue === 'staging-area' &&
		!isFirstPrompt &&
		source !== 'remix' &&
		creationContextParams?.source !== 'remix' &&
		!isRemixAction;
	const editorContentType = getChatContextContentType({
		editorContext,
		whiteboardContext,
		databaseContext,
		slidesContext,
	});
	const cwrEditContentType = isCwrEditFollowUp ? editorContentType : undefined;
	// Bookmark
	return {
		browserContext,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		dashboardEditorContext,
		additionalContext: {
			spaceCreateContext: await chatContextStoreActions.getSpaceCreateContext({
				isFirstPrompt,
				contentContext: contentContextValue,
			}),
			creationContext: {
				...(creationContextParams ? creationContextParams : {}),
				isStagingArea: contentContextValue === 'staging-area',
				productKey,
				// When `contentMauiId` or `mediaFileId` is present, we don't need to send the source
				// Ideally we want to set it upstream, but we need to ensure we don't break existing functionality
				source:
					editorContext?.contentMauiId || editorContext?.mediaFileId
						? 'remix'
						: // If source is explicitly passed, use that, otherwise rely on creationContextParams.source which is set at creation entry point (e.g. 'remix', 'ai-first-creation')
							(source ?? creationContextParams?.source),
				isViewMode: creationContextIsViewMode,
				isDraftLockedForEditing,
				...(creationContextIsViewMode &&
				editorContext?.isViewMode &&
				!creationContextParams?.experience
					? {
							experience: 'inline_view' as const,
							contentTypes: ['page'],
						}
					: {}),
				useGenericEditorSkill,
				additionalContext:
					additionalCreationContext || additionalCreationContextParams
						? {
								...additionalCreationContext,
								...additionalCreationContextParams,
							}
						: undefined,
				...(jiraContext && { jiraContext }),
				...(expValEquals('cc-maui-experiment', 'isEnabled', true) && {
					...(editorContext?.dynamicUiSource && {
						dynamicUiSource: {
							...editorContext.dynamicUiSource,
							...(creationContextParams?.dynamicUiSource?.fragmentAdf && {
								fragmentAdf: creationContextParams.dynamicUiSource.fragmentAdf,
							}),
						},
					}),
					...((isRemixAction ||
						source === 'remix' ||
						creationContextParams?.source === 'remix') && {
						dynamicUiType:
							editorContext?.dynamicUiType ||
							creationContextParams?.dynamicUiType ||
							lastAssistantMessageDynamicUiType,
					}),
					contentMauiId: editorContext?.contentMauiId ?? creationContextParams?.contentMauiId,
					mediaFileId: editorContext?.mediaFileId ?? creationContextParams?.mediaFileId,
				}),
				// Derive experience and contentTypes for CWR follow-up/edit prompts.
				...(isCwrEditFollowUp && {
					experience: 'cwr_edit',
					...(cwrEditContentType && { contentTypes: [cwrEditContentType] }),
				}),
				// Derive experience and contentTypes for Rovo Chat sidebar when a page/whiteboard/database/slides is open.
				...(!isCwrEditFollowUp &&
					getChatContextExperience({
						contentContextValue,
						existingExperience: creationContextParams?.experience,
						chatContextContentType: editorContentType,
						isViewMode,
					})),
			},
			userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			search_artifact: searchArtifact,
		},
		aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
		jiraWorkflowContext: chatContextStoreActions.getJiraWorkflowContext(),
		dashboardContext: chatContextStoreActions.getDashboardContext(),
		useCurrentBrowsingContext: chatContextStoreActions.useCurrentBrowsingContext(
			!!addCurrentPageAsContextUserPreference,
		),
	};
};

const handleSendMessageServiceCallFailure = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error: any,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	streamActionInfo: StreamActionInfo,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	sendMessageExperienceNameCompiled: string,
	trackingAttributes: SendMessageTrackingAttributes,
	updateCurrentConversation: (data: Partial<Conversation>) => void,
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void,
	confirmAbort: () => void,
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void,
	messageSources: Partial<MessageSource> | undefined,
	abortController: AbortController,
	hasLoggedInitiatedEvent: boolean,
	tmpAssistantMessageId: string,
	clearMessageResearch: (args_0: { messageId: string }) => void,
	getState: () => ChatStorePrivateState,
	conversationId: string,
	storeUserMessages: boolean,
) => {
	// Convert deep research related errors to DeepResearchError when deep research is enabled
	let convertedError = error;
	if (deepResearchEnabled) {
		convertedError = new DeepResearchError(error.message, {
			traceId: error.errorAttributes?.traceId,
			requestId: error.errorAttributes?.requestId,
		});
	}

	// Returns bad request error because agent is deactivated or user has no permission
	const isAgentDeactivated =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED);
	const isNoAgentPermission =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN) &&
		fg('convo-ai-enable-agents-use-permission');

	if (isAgentDeactivated || isNoAgentPermission) {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation({
			status: CHAT_STORE_STATUS.deactivatedAgentError,
			traces: [],
			errorCode: fg('convo-ai-enable-agents-use-permission')
				? error.errorAttributes.errorCode
				: undefined,
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
		return;
	} else if (error instanceof Error && error.name === 'AbortError') {
		// Check if the abort was programmatic or not. `abortController.signal.aborted` is a readonly field that is only set to true when called programmatically
		// The browser can abort requests to free up resources & save power (esp. on background tabs). When it does this, it **does not** set `abortController.signal.aborted` to true
		const isProgrammaticAbort = abortController.signal.aborted;

		experienceTracker.abort({
			name: sendMessageExperienceNameCompiled,
			reason: 'Aborted',
			attributes: {
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				isProgrammaticAbort,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'dismissed',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			subjectId: trackingAttributes.interactionSource,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation(
			clearStatus({ lastAgentMessageStatus: isProgrammaticAbort ? 'CANCELLED' : 'FAILED' }),
		);
		clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
		clearMessageResearch({ messageId: tmpAssistantMessageId });
		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

		confirmAbort();

		return;
	} else {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				fastModeEnabled,
				thinkDeeperEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		throw convertedError;
	}
};

export interface ProcessStreamParams {
	// Core stream params
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>, any, any>;
	conversationId: string;
	additionalAnalyticAttributes?: AdditionalAnalyticAttributes;
	// The assistant message ID to update during streaming.
	// For new messages: temporary ID that gets replaced with server ID.
	tmpAssistantMessageId: string;
	tmpHumanMessageId: string;
	// Message context
	prompt: DocNode;
	productKey: string | undefined;
	// The answer index to optimize message search.
	answerIndex: number;

	// Search Mode
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	deepResearchEnabled: boolean | undefined;
	webSearchEnabled: boolean | undefined;

	// State management
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	getState: () => ChatStorePrivateState;
	setMessages: ChatStoreContainerProps['setMessages'];
	getMessages: ChatStoreContainerProps['getMessages'];

	// Tracking & analytics
	traceId: string | undefined;
	sendMessageExperienceNameCompiled: string;
	trackingAttributes: SendMessageTrackingAttributes;
	experienceTracker: ExperienceTrackerAPI;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void;

	// Stream processing callbacks
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	processMessageStreamAction: ChatStoreContainerProps['processMessageStreamAction'];
	processMessageStreamComplete: ChatStoreContainerProps['processMessageStreamComplete'];
	processMessageStreamError: ChatStoreContainerProps['processMessageStreamError'];
	processMessageStreamActionClassification: ChatStoreContainerProps['processMessageStreamActionClassification'];
	containerOnTempMessageIdReplaced: ChatStoreContainerProps['onTempMessageIdReplaced'];
	updateResearchMessageId: ChatStoreContainerProps['updateResearchMessageId'];
	onTempMessageIdReplacedOverride: SendMessageConsumerPramas['onTempMessageIdReplacedOverride'];

	// Context & lifecycle
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'];
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void;
	streamActionInfo: StreamActionInfo;

	// Metrics
	stopInteractionMetricsTimeToFirstActionPart: ReturnType<typeof getMetricEndHelper>;
	stopInteractionMetricsTimeToFirstAnswerPart: ReturnType<typeof getMetricEndHelper>;
	stopInteractionMetricsTimeToActionCompletion: ReturnType<typeof getMetricEndHelper>;
	sendMessageLatencyMetrics: Metric;
	cancelStreamingMetrics: () => void;

	// Passed from the bridge-api
	promptStreamHook: PromptStreamHook | undefined;
	openChat?: boolean;
	openChatOnError?: boolean;

	messageSources: Partial<MessageSource> | undefined;
	hasLoggedInitiatedEvent: boolean;
	clearMessageResearch: (args_0: { messageId: string }) => void;
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	viewDeadAirBroker: ChatStoreContainerProps['viewDeadAirBroker'];
	minionAlias?: string;
	agentId?: string;
	agentName?: string;
	agentIdentityAccountId?: string | null | undefined;
	creationContextParams?: CreationContextParams;
	storeUserMessages: boolean;

	/**
	 * Where the most recently inserted skill placeholder was selected from.
	 * Stamped onto each `skillsInvoked` entry whose `invocationType` is `'explicit'`.
	 */
	skillSelectionSource?: SkillSelectionSource;

	// Reconnection support
	assistanceService: ChatStoreContainerProps['assistanceService'];
	abortController: AbortController;
	getIsChatMounted?: ChatStoreContainerProps['getIsChatMounted'];
	metricsStartTime: number;

	// Task status callback
	onTaskStatusUpdate?: (streamMessage: StreamTaskStatus) => void;
	onTodoSnapshot?: (params: { todos: TodoItem[] }) => void;

	// Guard callbacks — called when the backend signals prompt/response redaction or blocking.
	// The backend always follows blocking events with an ERROR to terminate the stream, which
	// should be treated as a normal end of the guard flow rather than a real error.
	onPromptRedacted?: (streamMessage: Parameters<typeof isStreamPromptRedaction>[0]) => void;
	onPromptBlocked?: (streamMessage: Parameters<typeof isStreamPromptBlock>[0]) => void;
	onResponseRedacted?: (streamMessage: Parameters<typeof isStreamResponseRedaction>[0]) => void;
	onResponseBlocked?: (streamMessage: Parameters<typeof isStreamResponseBlock>[0]) => void;
	onAttachmentBlocked?: (streamMessage: Parameters<typeof isStreamAttachmentBlock>[0]) => void;
}

export type ProcessResearchStreamMessageParams = {
	streamMessage: StreamMessage;
	tmpAssistantMessageId: string;
	conversationId: string;
	deepResearchEnabled: boolean | undefined;
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	/** Mutable state shared between calls; updated in-place */
	researchState: ResearchStreamState;
};

/**
 * Handles all research-store updates for a single stream message.
 *
 * Encapsulates:
 * - Implicit round detection for think-deeper (clientRoundCounter / hasAccumulatedAnswerContent)
 * - roundId override on research payload messages
 * - processStreamResearch dispatch for all applicable message types
 * - isStreamResearchPlan → processStreamResearch
 * - isStreamResearchStart → sets deepResearchInvoked flag (returned as boolean)
 * - isStreamAnswerPart → marks hasAccumulatedAnswerContent = true
 *
 * Returns `true` when deep research was detected as invoked (isStreamResearchStart),
 * so the caller can update its own tracking attributes.
 */
export const processResearchStreamMessage = ({
	streamMessage,
	tmpAssistantMessageId,
	conversationId,
	deepResearchEnabled,
	getMessages,
	setMessages,
	processStreamResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	researchState,
}: ProcessResearchStreamMessageParams): { deepResearchInvoked: boolean } => {
	let deepResearchInvoked = false;

	if (
		isStreamResearchStart(streamMessage) &&
		// The same message_template is used for thinking deeper + deep research
		// so we need to check the specific trace message to differentiate them
		streamMessage.message.content === 'Starting deep research execution'
	) {
		deepResearchInvoked = true;
	}

	// Implicit round detection for think deeper:
	// When answer content has accumulated (from ANSWER_PART messages) and new
	// research step information arrives, we know a new round is starting.
	// We retroactively create a research plan for the completed round using
	// the accumulated answer content, clear the streamed content, and increment
	// the round counter so subsequent steps are grouped under the new round.
	if (researchState.hasAccumulatedAnswerContent && isStreamResearchPayload(streamMessage)) {
		// Read the accumulated answer content from the assistant message
		const accumulatedContent = getStoreAssistantMessageContent(
			getMessages(conversationId),
			tmpAssistantMessageId,
		);

		// Create a research plan for the completed round with the accumulated content
		addImplicitResearchPlan({
			messageId: tmpAssistantMessageId,
			content: accumulatedContent,
			roundId: researchState.clientRoundCounter,
		});

		// Increment round counter for the new incoming steps
		researchState.clientRoundCounter++;

		// Clear the assistant message content so the new round starts fresh
		const clearedMessages = clearStoreAssistantMessageContent(
			getMessages(conversationId),
			tmpAssistantMessageId,
		);
		setMessages(conversationId, clearedMessages);

		// Reset tracking flags
		researchState.hasAccumulatedAnswerContent = false;
		resetHasReceivedAnswerPart({ messageId: tmpAssistantMessageId });
	}

	if (
		isStreamResearchPayload(streamMessage) ||
		isStreamResponse(streamMessage) ||
		isStreamAnswerPart(streamMessage) ||
		isStreamReasoningPart(streamMessage) ||
		isStreamSkill(streamMessage) ||
		isStreamThinkDeeperTriggered(streamMessage)
	) {
		// When implicit rounds are active, override roundId on research step messages
		// so they are grouped under the current client-managed round
		if (
			researchState.clientRoundCounter > 0 &&
			isStreamResearchPayload(streamMessage) &&
			'message' in streamMessage &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			'roundId' in (streamMessage as any).message
		) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(streamMessage as any).message.roundId = researchState.clientRoundCounter;
		}

		processStreamResearch({
			messageId: tmpAssistantMessageId,
			streamMessage,
			isDeepResearch: deepResearchEnabled ?? false,
		});
	}

	if (isStreamResearchPlan(streamMessage)) {
		processStreamResearch({
			messageId: tmpAssistantMessageId,
			streamMessage,
			isDeepResearch: deepResearchEnabled ?? false,
		});
	}

	// Track that answer content has accumulated so implicit round detection
	// can detect a new round starting when the next research payload arrives
	if (isStreamAnswerPart(streamMessage)) {
		researchState.hasAccumulatedAnswerContent = true;
	}

	return { deepResearchInvoked };
};

export const processStream = async ({
	prompt,
	generator,
	productKey,
	answerIndex,
	tmpHumanMessageId,
	tmpAssistantMessageId,
	thinkDeeperEnabled,
	fastModeEnabled,
	deepResearchEnabled,
	webSearchEnabled,
	sendMessageExperienceNameCompiled,
	traceId,
	updateCurrentConversation,
	trackingAttributes,
	conversationId,
	getState,
	promptStreamHook,
	openChat,
	openChatOnError,
	experienceTracker,
	setMessages,
	getMessages,
	processStreamResearch,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamError,
	processMessageStreamActionClassification,
	containerOnTempMessageIdReplaced,
	updateResearchMessageId,
	sendAnalyticsEvent,
	chatContextStoreActions,
	stopInteractionMetricsTimeToFirstActionPart,
	stopInteractionMetricsTimeToFirstAnswerPart,
	stopInteractionMetricsTimeToActionCompletion,
	broadcastStreamLifecycle,
	sendMessageLatencyMetrics,
	cancelStreamingMetrics,
	onTempMessageIdReplacedOverride,
	streamActionInfo,
	trackAIMAUEvent,
	messageSources,
	additionalAnalyticAttributes,
	hasLoggedInitiatedEvent,
	clearMessageResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	viewDeadAirBroker,
	minionAlias,
	agentId,
	agentName,
	agentIdentityAccountId,
	creationContextParams,
	storeUserMessages,
	skillSelectionSource,
	assistanceService,
	abortController,
	getIsChatMounted,
	metricsStartTime,
	onTaskStatusUpdate,
	onTodoSnapshot,
	onPromptRedacted,
	onPromptBlocked,
	onResponseRedacted,
	onResponseBlocked,
	onAttachmentBlocked,
}: ProcessStreamParams): Promise<
	| {
			hasReceivedFinalResponse: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			hasAbortedForImplicitThinkDeeper?: undefined;
			implicitThinkDeeperSendMessageMetrics?: undefined;
			implicitThinkDeeperExperienceName?: undefined;
	  }
	| {
			hasReceivedFinalResponse: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			hasAbortedForImplicitThinkDeeper: boolean;
			implicitThinkDeeperSendMessageMetrics: Metric | null;
			implicitThinkDeeperExperienceName: string | null;
	  }
> => {
	updateCurrentConversation({
		traces: [],
	});

	// Set to true when a guard event (PROMPT_REDACTION or PROMPT_BLOCKED) is received.
	// The backend always follows these events with an ERROR event to terminate the stream,
	// which should be treated as a successful end of the guard flow, not a real error.
	let guardEventReceived = false;

	let hasReceivedFinalResponse = false;
	let hasReceivedFollowUp = false;
	let hasReceivedActionComplete = false;
	let hasReceivedActionClassification = false;
	let isLuminaAnswerGenerationType = false;
	let agentMessageId: string | undefined;
	let lastEventId: string | undefined;
	let isReconnectSupported = false;
	// Guards against repeated metric switches if multiple REASONING_PART messages arrive.
	let hasAbortedForImplicitThinkDeeper = false;
	// Think-deeper metrics started when implicit think-deeper is detected mid-stream. Null otherwise.
	let implicitThinkDeeperSendMessageMetrics: ReturnType<
		typeof messageLatencyMetrics.createSendMessageMetrics
	> | null = null;
	let implicitThinkDeeperTimeToFirstAnswerPartMetrics: ReturnType<
		typeof messageLatencyMetrics.createTimeToFirstAnswerPartMetrics
	> | null = null;
	// The experience name for the reliability SLO when implicit think-deeper is detected. Null otherwise.
	let implicitThinkDeeperExperienceName: string | null = null;

	// Client-side implicit round tracking for think deeper.
	// Passed by reference into processResearchStreamMessage so state is shared.
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	// Gate the details-tag preprocessor to 3P-agent conversations only.
	// Lazily initialised on the first chunk: the conversation's agent metadata
	// is set by `createConversationOptimistically` which may run after this
	// function is defined but before the first chunk arrives.
	let processStreamMarkdownInstance: ReturnType<typeof createStreamMarkdownProcessor> | null = null;
	let processStreamMarkdownMultiTagInstance: ReturnType<
		typeof createStreamMarkdownMultiTagProcessor
	> | null = null;
	const getShouldPreprocessDetails = () =>
		shouldPreprocessDetailsForConversation(getState(), conversationId);
	const getProcessStreamMarkdown = () => {
		if (!processStreamMarkdownInstance) {
			processStreamMarkdownInstance = createStreamMarkdownProcessor({
				shouldPreprocessDetails: getShouldPreprocessDetails(),
			});
		}
		return processStreamMarkdownInstance;
	};
	const getProcessStreamMarkdownMultiTag = () => {
		if (!processStreamMarkdownMultiTagInstance) {
			processStreamMarkdownMultiTagInstance = createStreamMarkdownMultiTagProcessor({
				shouldPreprocessDetails: getShouldPreprocessDetails(),
			});
		}
		return processStreamMarkdownMultiTagInstance;
	};

	let latestStreamMessage: StreamMessage | undefined;

	const streamStats = getStreamStats();
	// Between-chunks inactivity watchdog (logs once per stall, re-arms on chunks)
	const inactivity = createBetweenChunksInactivityLogger({
		baseMs: 90_000,
		sendAnalyticsEvent: (event: AnalyticsEventPayload) => {
			sendAnalyticsEvent({
				...event,
				attributes: {
					...event.attributes,
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
				},
			});
		},
	});

	// Dead air tracking - monitors gaps between meaningful stream messages
	const deadAirTracker = createDeadAirTracker({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		deepResearchEnabled: deepResearchEnabled ?? false,
		thinkDeeperEnabled: thinkDeeperEnabled ?? false,
		fastModeEnabled: fastModeEnabled ?? false,
		webSearchEnabled: webSearchEnabled ?? false,
		isAIFC: streamActionInfo.isAction,
		sendAnalyticsEvent,
	});

	viewDeadAirBroker?.start({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		reasoningMode: deepResearchEnabled
			? 'deep-research'
			: thinkDeeperEnabled
				? 'think-deeper'
				: fastModeEnabled
					? 'fast'
					: 'auto',
		isAIFCEnabled: streamActionInfo.isAction,
		isWebSearchEnabled: webSearchEnabled ?? false,
	});

	// Reset the stream response recording for a new stream session
	let canRecordStreamResponses = false;
	try {
		const isLocalStorageDebuggerEnabled =
			localStorage.getItem('__rovoChatResponseDebugger__') === 'true' &&
			fg('rovo_debug_network_response_localstorage');
		const isDebuggerEnabled =
			window.top?.__rovoChatResponseDebugger__ || isLocalStorageDebuggerEnabled;
		canRecordStreamResponses = Boolean(isDebuggerEnabled && fg('editor-4209_sse_stream_debug'));
	} catch {
		canRecordStreamResponses = false;
	}

	if (canRecordStreamResponses) {
		resetStreamResponseRecording();
	}

	// Arm the inactivity watchdog
	inactivity?.arm();

	/*****************************
	 *
	 * Stream processing START
	 *
	 *****************************/

	const streamToIterate = createChatReconnectableStream({
		generator,
		conversationId,
		abortController,
		assistanceService,
		sendAnalyticsEvent,
		experienceTracker,
		getMessageId: () => agentMessageId,
		canReconnect: () =>
			!!(agentMessageId && isReconnectSupported) &&
			FeatureGates.getExperimentValue('rovo_chat_reconnect_all_experiment', 'isEnabled', false),
		updateCurrentConversation,
		getIsChatMounted,
		analyticsAttributes: {
			isDeepResearch: deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
		},
		onBeforeFirstReconnect: () => {
			const chat = getMessages(conversationId);
			const alreadySwapped = findStoreMessageIndex(chat, agentMessageId!) >= 0;

			if (!alreadySwapped) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageId({
						chat,
						oldId: tmpAssistantMessageId,
						newId: agentMessageId!,
					}),
				);
				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId!,
				});
			}

			tmpAssistantMessageId = agentMessageId!;
		},
		onReconnected: () => {
			inactivity?.arm();
		},
		onFirstChunkAfterReconnect: () => {
			deadAirTracker?.resetTimestamp();
		},
	});

	try {
		for await (const streamObj of streamToIterate) {
			// Track the last event ID for potential reconnection (legacy path)
			if (isSSEMessage(streamObj) && streamObj.id) {
				lastEventId = streamObj.id;
			}

			const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

			// Record the raw stream message for debugging purposes
			// This allows developers to copy/paste SSE responses for examples/tests
			if (canRecordStreamResponses) {
				recordStreamMessage(streamMessage);
			}

			latestStreamMessage = streamMessage;
			deadAirTracker?.update(streamMessage);
			streamStats.update(streamMessage);
			// any chunk = proof of life
			inactivity?.onChunk();

			// Note: We do NOT skip stream messages during SWITCHING.
			// Stream processing continues normally until FAST_MODE_ENABLED is received,
			// at which point research steps are cleared and status is set to SWITCHED.

			// Usually the first stream part
			if (isConversationChannelDataMessage(streamMessage)) {
				agentMessageId = streamMessage.assistant_message_id;

				updateCurrentConversation({
					name: streamMessage.name,
					lastMessageTimestamp: streamMessage.last_message_timestamp ?? new Date().toISOString(),
					timeCreated: streamMessage.time_created,
					...(streamMessage.scopes && { scopes: streamMessage.scopes }),
				});

				trackingAttributes.instrumentationId = agentMessageId;
				trackingAttributes.messageId = agentMessageId;

				if (
					agentMessageId &&
					agentMessageId !== tmpAssistantMessageId &&
					fg('cc-cwr-fix-finalizing-cancelled-request')
				) {
					const oldTmpId = tmpAssistantMessageId;
					setMessages(
						conversationId,
						updateStoreAssistantMessageId({
							chat: getMessages(conversationId),
							oldId: oldTmpId,
							newId: agentMessageId,
						}),
					);
					updateResearchMessageId({
						messageId: oldTmpId,
						newMessageId: agentMessageId,
					});
					tmpAssistantMessageId = agentMessageId;
					(onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced)?.(
						oldTmpId,
						agentMessageId,
					);
				}

				trackingAttributes.invokedFrom =
					creationContextParams?.source === 'remix' ? creationContextParams?.source : undefined;

				const remixNodeTypes = creationContextParams?.dynamicUiSource
					? getTopLevelNodeTypes(
							creationContextParams.dynamicUiSource.fullAdf,
							creationContextParams.dynamicUiSource.startLocalId ?? '',
							creationContextParams.dynamicUiSource.endLocalId ?? '',
						)
					: undefined;
				const remixWordCount = creationContextParams?.dynamicUiSource?.fragmentAdf
					? countFragmentWords(creationContextParams.dynamicUiSource.fragmentAdf)
					: undefined;
				if (remixNodeTypes) trackingAttributes.remixNodeTypes = remixNodeTypes;
				if (remixWordCount !== undefined) trackingAttributes.remixWordCount = remixWordCount;

				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: streamMessage.last_message_timestamp,
					singleInstrumentationID: agentMessageId,
					deepResearchEnabled: deepResearchEnabled ?? false,
					thinkDeeperEnabled: thinkDeeperEnabled ?? false,
					fastModeEnabled: fastModeEnabled ?? false,
					webSearchEnabled: webSearchEnabled ?? false,
					pageContext: trackingAttributes.pageContext,
					viewContext: trackingAttributes.viewContext,
					invokedFrom: trackingAttributes.invokedFrom,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
						...(remixNodeTypes !== undefined && { remixNodeTypes }),
						...(remixWordCount !== undefined && { remixWordCount }),
					},
				});
				hasLoggedInitiatedEvent = true;
			} else if (isScopeDeterminedMessage(streamMessage)) {
				updateCurrentConversation({ scopes: streamMessage.scopes });
			} else {
				const conversation = getState().conversations[getState().currentConversationId];
				if (conversation) {
					if (conversation.status !== CHAT_STORE_STATUS.typingResponse) {
						updateCurrentConversation({
							status: CHAT_STORE_STATUS.typingResponse,
						});
					}
				}
			}

			if (isStreamReconnectSupportPart(streamMessage)) {
				isReconnectSupported = true;
			}

			if (isStreamAnswerGenerationType(streamMessage) && fg('rovo_chat_enable_lumina')) {
				isLuminaAnswerGenerationType = streamMessage.message.answer_generation_type === 'LUMINA';
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerGenerationType(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage.message.answer_generation_type,
					),
				);
			}

			{
				const { deepResearchInvoked } = processResearchStreamMessage({
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					deepResearchEnabled,
					getMessages,
					setMessages,
					processStreamResearch,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					researchState,
				});
				if (deepResearchInvoked) {
					trackingAttributes.deepResearchInvoked = true;
				}
			}

			// The backend autonomously triggered thinking mode (implicit think-deeper) even though
			// the user did not explicitly enable it. Cancel the auto-mode browser metrics so these
			// slow requests don't count against the <16s/<30s SLOs, and re-attribute them to the
			// think-deeper metrics instead.
			if (
				!thinkDeeperEnabled &&
				!hasAbortedForImplicitThinkDeeper &&
				isStreamThinkDeeperTriggered(streamMessage)
			) {
				hasAbortedForImplicitThinkDeeper = true;
				// Cancel auto-mode metrics so they are not recorded in the SLOs
				stopInteractionMetricsTimeToFirstAnswerPart('cancel');
				sendMessageLatencyMetrics.cancel();
				// Re-attribute to think-deeper metrics (100s SLO target)
				implicitThinkDeeperSendMessageMetrics = messageLatencyMetrics.createSendMessageMetrics({
					thinkDeeperEnabled: true,
				});
				implicitThinkDeeperTimeToFirstAnswerPartMetrics =
					messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
						thinkDeeperEnabled: true,
					});
				implicitThinkDeeperSendMessageMetrics.start({ startTime: metricsStartTime });
				implicitThinkDeeperTimeToFirstAnswerPartMetrics.start({ startTime: metricsStartTime });
				// Abort the auto-mode reliability SLO experience and start the think-deeper one
				// so implicit think-deeper requests don't count against the auto-mode reliability SLO.
				implicitThinkDeeperExperienceName = sendMessageExperienceName({
					thinkDeeperEnabled: true,
				});
				experienceTracker.abort({
					name: sendMessageExperienceNameCompiled,
					reason: 'Re-attributed to think-deeper reliability SLO',
				});
				experienceTracker.start({
					name: implicitThinkDeeperExperienceName,
					id: conversationId,
					startTime: metricsStartTime,
					attributes: {
						conversationId,
						interactionSource: trackingAttributes.interactionSource,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
					},
				});
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!trackingAttributes.requestId && 'metadata' in streamMessage) {
				trackingAttributes.requestId = streamMessage.metadata?.request_id ?? undefined;
			}

			if (isStreamResponse(streamMessage)) {
				trackingAttributes.interactionModeExecuted =
					getInteractionModeExecutedFromStreamResponse(streamMessage);
			}

			// Handle MEMORY_UPDATE trace messages - create a persistent StoreMemoryUpdate message
			if (
				isStreamMemoryUpdate(streamMessage) &&
				FeatureGates.getExperimentValue('memory_management_ux', 'isEnabled', false)
			) {
				const originalContent = streamMessage.message.content;
				const isMemoryTraceContentEmpty = !originalContent;
				let memoryContent = originalContent;
				let isMemoryContentAfterParsingEmpty = false;

				// Parse memory content: remove "Update memory:" prefix and trim
				if (memoryContent) {
					memoryContent = memoryContent.replace(/^update\s+memory:\s*/i, '').trim();
					isMemoryContentAfterParsingEmpty = !memoryContent;
				}

				const memoryUpdateMessage = {
					type: 'MEMORY_UPDATE' as const,
					content: memoryContent || undefined, // Use undefined instead of empty string
				};

				// Track memory update parsing events
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'parsed',
					actionSubject: 'memoryUpdate',
					attributes: {
						isMemoryTraceContentEmpty,
						isMemoryContentAfterParsingEmpty,
						conversationId,
					},
				});

				setMessages(
					conversationId,
					addMemorytoLatestAssistantMessage({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						memory: memoryUpdateMessage,
						useIndex: answerIndex,
					}),
				);
			}

			// Usually the second stream part, before anything else occurs
			if (isStreamTrace(streamMessage)) {
				if (!deepResearchEnabled) {
					// we do not want traces that are not marked for deep research to be accumulated when starting research,
					// the ones that are marked are processed above in processResearchStreamMessage
					updateCurrentConversation({
						traces: dedupeTraceMessagesWithLatest([
							...getConversationTraces(getState(), conversationId),
							getTraceFromStreamMessage(streamMessage),
						]),
					});
				}

				promptStreamHook?.onTraceMessage?.(streamMessage, tmpAssistantMessageId, openChat);

				setMessages(
					conversationId,
					addRequestIdToLatestHumanMessage(
						getMessages(conversationId),
						trackingAttributes.requestId,
					),
				);
			}

			// Handle Answer Now fast mode traces for extended thinking
			// Answer Now feature - handle fast mode traces
			if (isStreamFastModeResponseAvailable(streamMessage) && agentMessageId) {
				// Fast mode response is available - show Answer Now button
				// Store both IDs in AVAILABLE status:
				// - tmpMessageId: for store operations (finding/updating the message)
				// - remoteMessageId: for the Answer Now API call
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{
							type: AnswerNowStatusType.AVAILABLE,
							tmpMessageId: tmpAssistantMessageId,
							remoteMessageId: agentMessageId,
						},
					),
				);
			}

			if (isStreamFastModeEnabled(streamMessage)) {
				// Fast mode has been enabled:
				// 1. Set status to SWITCHED (hides button)
				// 2. Clear research steps for this message
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{ type: AnswerNowStatusType.SWITCHED },
					),
				);
				// Clear research steps when switching to fast mode
				clearMessageResearch({ messageId: tmpAssistantMessageId });
			}

			if (isStreamRemoveAnswerNow(streamMessage)) {
				clearAnswerNowStatusForMessageIfSet(
					conversationId,
					tmpAssistantMessageId,
					getMessages,
					setMessages,
				);
			}

			// isStreamResearchPlan is handled by processResearchStreamMessage (called earlier in this loop)

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			let commonExperienceAttributes: Record<string, any> = {
				llmDebugLogsEnabled: storeUserMessages,
			};
			if (isStreamActionClassification(streamMessage)) {
				promptStreamHook?.onActionClassification?.(
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
				);

				const actionKey = streamMessage.message.action_key;
				hasReceivedActionClassification = true;
				const actualContentType = getAIFCContentType({ key: actionKey });
				commonExperienceAttributes = {
					...commonExperienceAttributes,
					actionKey,
					contentType: actualContentType,
					isStagingArea: chatContextStoreActions.getIsStagingArea(),
					humanMessageCount: getMessages(conversationId).filter((m) => m.type === 'HUMAN').length,
					product: productKey,
				};

				if (trackingAttributes.forcedContentType) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'compared',
						actionSubject: 'forcedContentType',
						actionSubjectId: 'contentTypeMatch',
						attributes: {
							forcedContentType: trackingAttributes.forcedContentType,
							actualContentType,
							matched: actualContentType.endsWith(`-${trackingAttributes.forcedContentType}`),
							actionKey,
						},
					});
				}

				const isUpdateAction = isUpdatableAction({ key: actionKey });
				if (
					isUpdateAction &&
					(!commonExperienceAttributes.isStagingArea ||
						commonExperienceAttributes.humanMessageCount > 1)
				) {
					const rootExperienceName = commonExperienceAttributes.isStagingArea
						? ExperienceName.AIFC_UPDATE_CONTENT
						: ExperienceName.UPDATE_CONTENT_IN_CHAT;

					startUpdateActionExperiences({
						experienceTracker,
						conversationId,
						rootExperienceName,
						commonExperienceAttributes,
					});
				}

				const isCreateAction = isCreatableAction({ key: actionKey });
				if (isCreateAction) {
					// initial prompt from staging area has its own AIFC_GENERATE_CONTENT experience - dont start this one
					if (
						!commonExperienceAttributes.isStagingArea ||
						commonExperienceAttributes.humanMessageCount > 1
					) {
						experienceTracker.start({
							name: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							id: conversationId,
							collect: createAifcCreateInChatExperienceCollect(),
							attributes: commonExperienceAttributes,
						});

						experienceTracker.start({
							name: ExperienceName.AIFC_MESSAGE,
							id: conversationId,
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
						experienceTracker.start({
							name: ExperienceName.PREVIEW_CARD_RENDER,
							id: conversationId,
							timeout: deepResearchEnabled ? undefined : 60_000, // 60 second timeout
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
						// Product AIFC integration is responsible for succeeding this experience.
						experienceTracker.start({
							name: ExperienceName.AIFC_ALL_DATA_HANDLED,
							id: conversationId,
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
					} else {
						// update AIFC_GENERATE_CONTENT now that intent detection is complete. This experience starts as the user sends the message on the initial prompt screen
						experienceTracker.updateAttributes({
							name: ExperienceName.AIFC_GENERATE_CONTENT,
							attributes: commonExperienceAttributes,
						});

						experienceTracker.updateAttributes({
							name: ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
							attributes: commonExperienceAttributes,
						});
					}
				}
			}

			if (
				(isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) &&
				fg('aifc_create_enabled')
			) {
				// Update action tracking info
				if (!streamActionInfo.isAction) {
					streamActionInfo.isAction = true;
					streamActionInfo.actionKey = streamMessage.message.key;
				}

				// track attribute for action prepare
				if (
					isStreamActionPrepare(streamMessage) &&
					expValEquals('cc-maui-experiment', 'isEnabled', true)
				) {
					const actionData = streamMessage.message.data as
						| { dynamicUiSubtype?: string }
						| undefined;
					experienceTracker.updateAttributes({
						name: ExperienceName.AIFC_MESSAGE,
						attributes: { dynamicUiSubtype: actionData?.dynamicUiSubtype },
					});

					// Remix flows skip ACTION_CLASSIFICATION and go straight to ACTION_PREPARE,
					// so we start PREVIEW_CARD_RENDER here for remix flows.
					// Only start when rovo chat is mounted/visible to the user.
					if (
						getIsChatMounted?.() &&
						isRemixGeneratedFlow({
							key: streamMessage.message.key,
							data: streamMessage.message.data,
						})
					) {
						const remixActionKey = streamMessage.message.key;
						const actualContentType = getAIFCContentType({ key: remixActionKey });
						experienceTracker.start({
							name: ExperienceName.PREVIEW_CARD_RENDER,
							id: conversationId,
							timeout: 60_000,
							attributes: {
								...commonExperienceAttributes,
								actionKey: remixActionKey,
								contentType: actualContentType,
								isStagingArea: chatContextStoreActions.getIsStagingArea(),
								humanMessageCount: getMessages(conversationId).filter((m) => m.type === 'HUMAN')
									.length,
								product: productKey,
								featureArea: 'remix',
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								remixType: getDynamicUiType(streamMessage.message as any),
							},
						});
					}
				}

				if (isStreamActionPart(streamMessage)) {
					const actionData = streamMessage.message.data as
						| { dynamicUiSubtype?: string }
						| undefined;
					stopInteractionMetricsTimeToFirstActionPart('stop', {
						actionType: 'action-part',
						conversationId,
						actionPartKey: getActionPartKeyForMetrics(
							streamMessage.message.key,
							creationContextParams?.source,
							actionData?.dynamicUiSubtype,
						),
						interactionSource: messageSources?.interactionSource,
						remixType: creationContextParams?.dynamicUiType,
					});
				}

				if (fg('aifc_set_accumulated_data_on_message')) {
					const actionKey = streamMessage.message.key;
					const lastAction = findLastAssistantAction(
						getMessages(conversationId),
						(action) => action.key === actionKey,
					);

					if (lastAction && isAccumulatedAction(actionKey)) {
						const isAccumulatedActionType = getAccumulatedActionTypeGuard(actionKey);
						if (isAccumulatedActionType(streamMessage.message.data)) {
							const primaryAccumulatedData = getPrimaryAccumulatedValue(lastAction);
							if (primaryAccumulatedData?.value) {
								streamMessage.message.data.previousAccumulatedData = primaryAccumulatedData.value;
							}
						}
					}
				}

				processMessageStreamAction?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					undefined,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
					agentId
						? {
								id: agentId,
								name: agentName ?? '',
								identityAccountId: agentIdentityAccountId ?? null,
							}
						: undefined,
				);

				promptStreamHook?.onActionPart?.(
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					openChat,
				);

				const updatedMessages = addActionPart({
					chat: getMessages(conversationId),
					messageId: tmpAssistantMessageId,
					actionPart: streamMessage,
					...(fg('aifc_set_accumulated_data_on_message')
						? { actionUpdaterOverride: customActionUpdater }
						: {}),
				});

				if (isStreamActionPrepare(streamMessage)) {
					const actionMessage = streamMessage.message;
					// Abort the remix-edit-existing UFO experience if the streamed action is not remix.
					if (!isRemixGeneratedFlow(actionMessage)) {
						remixEditExistingExperience.abort({ metadata: { reason: 'non-remix-action' } });
					}
					setMessages(conversationId, [
						...updatedMessages,
						{ type: 'ACTION_PREPARE', action: actionMessage },
					]);
				} else {
					setMessages(conversationId, updatedMessages);
				}
			}

			const isAIFCEnabled = fg('aifc_create_enabled');
			const canHandleStreamActionClassification = fg(
				'platform_editor_ai_promptversion_in_analytics',
			);
			if (
				isStreamActionClassification(streamMessage) &&
				isAIFCEnabled &&
				canHandleStreamActionClassification &&
				streamMessage.message.metadata
			) {
				// Send ACTION_CLASSIFICATION metadata to the Editor Rovo bridge
				processMessageStreamActionClassification?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
				);
			}

			if (isStreamActionComplete(streamMessage) && fg('aifc_create_enabled')) {
				hasReceivedActionComplete = true;
				processMessageStreamComplete?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
				);
				const actionMessage = streamMessage.message;
				setMessages(conversationId, [
					...getMessages(conversationId),
					{ type: 'ACTION_COMPLETE', action: actionMessage },
				]);
				experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
			}

			if (isStreamAnswerPart(streamMessage)) {
				stopInteractionMetricsTimeToFirstAnswerPart('stop', {
					answerType: 'stream-answer-part',
					conversationId,
				});
				// Stop the implicit think-deeper time-to-first-answer-part metric if active
				implicitThinkDeeperTimeToFirstAnswerPartMetrics?.stop({
					customData: { answerType: 'stream-answer-part', conversationId },
				});

				// Clear Answer Now status when ANSWER_PART is received
				// This handles the case where the stream starts answering without user clicking Answer Now
				clearAnswerNowStatusForMessageIfSet(
					conversationId,
					tmpAssistantMessageId,
					getMessages,
					setMessages,
				);

				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				let processedStreamMessage = streamMessage;
				let hasMarkdownData = false;

				if (!isLuminaAnswerGenerationType && fg('rovo_enable_component_streaming')) {
					if (fg('ai_mate_handle_multi_tags_in_stream_msg')) {
						const result = getProcessStreamMarkdownMultiTag()(processedStreamMessage);
						processedStreamMessage = result.processedStreamMessage;
						if (Object.keys(result.markdownData).length > 0) {
							hasMarkdownData = true;
							setMessages(
								conversationId,
								addAnswerPartWithMarkdownDataMap({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									markdownData: result.markdownData,
									useIndex: answerIndex,
								}),
							);
						}
					} else {
						const result = getProcessStreamMarkdown()(processedStreamMessage);
						processedStreamMessage = result.processedStreamMessage;
						if (result.markdownData) {
							hasMarkdownData = true;
							setMessages(
								conversationId,
								addAnswerPartWithMarkdownData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									markdownData: result.markdownData,
									useIndex: answerIndex,
								}),
							);
						}
					}
				}

				if (!isLuminaAnswerGenerationType) {
					promptStreamHook?.onAnswerPart?.(
						processedStreamMessage,
						tmpAssistantMessageId,
						conversationId,
					);
				}
				if (!hasMarkdownData) {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: streamMessage,
							useIndex: answerIndex,
						}),
					);
				}

				// hasAccumulatedAnswerContent tracking is handled by processResearchStreamMessage
			}

			/**********************************************************
			 *
			 * FINAL_RESPONSE processing
			 *
			 **********************************************************/
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				const humanMessage = streamMessage?.message?.human_message;
				const humanMessageId = humanMessage?.id?.toString();
				const agentMessageId = streamMessage?.message?.message?.id.toString();
				// Record the backend-assigned conversation ID on the optimistic entry so the
				// space sidenav merge logic can dedupe between the local optimistic entry
				// (client UUID) and the API-returned entry (backend ID), and display the
				// correct name from the chat store rather than the timestamp fallback.
				const backendConversationId = streamMessage?.message?.message?.conversation_channel_id;
				if (backendConversationId) {
					updateCurrentConversation({ backendConversationId });
				}
				const minionOrchestrationOutputs = streamMessage?.message?.message?.minion_outputs;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				const { processedStreamMessage, markdownData } = !isLuminaAnswerGenerationType
					? processStreamResponseMarkdown({
							streamMessage,
							messageId: agentMessageId,
							conversationId,
							sendAnalyticsEvent,
							shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
								getState(),
								conversationId,
							),
						})
					: { processedStreamMessage: streamMessage, markdownData: {} };

				// Finish view dead air tracking before rendering final response
				// This ensures the last tracked interval is up to FINAL_RESPONSE arrival,
				// and any DOM updates from replaceAnswerData are not tracked
				viewDeadAirBroker?.finish('completed');

				promptStreamHook?.onFinalResponse?.(processedStreamMessage, tmpAssistantMessageId);
				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
						onMessageIdChanged: onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced,
					}),
				);

				if (humanMessageId) {
					setMessages(
						conversationId,
						updateHumanMessageId({
							chat: getMessages(conversationId),
							oldId: tmpHumanMessageId,
							newId: humanMessageId,
							parentMessageId: humanMessage?.parent_message_id,
						}),
					);
				}

				if (fg('aifc_create_enabled')) {
					const allMessages = getMessages(conversationId);
					const isValidAction = handleStagingAreaActionValidation(
						allMessages,
						conversationId,
						traceId,
						chatContextStoreActions,
						experienceTracker,
						getMessages,
						setMessages,
						sendMessageLatencyMetrics,
						cancelStreamingMetrics,
						commonExperienceAttributes,
						productKey,
						minionOrchestrationOutputs,
					);

					if (!isValidAction) {
						return { hasReceivedFinalResponse, hasLoggedInitiatedEvent, guardEventReceived };
					}

					const finalActions = processedStreamMessage.message.message.actions || [];
					if (finalActions.length > 0) {
						const firstActionData = finalActions[0]?.data as
							| { dynamicUiSubtype?: string }
							| undefined;
						stopInteractionMetricsTimeToActionCompletion('stop', {
							conversationId,
							actionType: 'final-actions',
							actionCount: finalActions.length,
							actionKeys: finalActions.map((a) => a.key),
							actionPartKey: getActionPartKeyForMetrics(
								streamActionInfo.actionKey,
								creationContextParams?.source,
								firstActionData?.dynamicUiSubtype,
							),
							interactionSource: messageSources?.interactionSource,
							remixType: creationContextParams?.dynamicUiType,
						});
					} else {
						stopInteractionMetricsTimeToActionCompletion('cancel');
					}
				}

				// Update tracking attributes from FINAL_RESPONSE using the shared extractor.
				const streamMeta = extractStreamMeta(
					streamMessage as StreamResponse,
					skillSelectionSource,
					expValEquals('cc-maui-experiment', 'isEnabled', true),
				);

				if (!hasLoggedInitiatedEvent) {
					trackingAttributes.messageId = agentMessageId;
					trackingAttributes.instrumentationId = agentMessageId;
				}
				trackingAttributes.humanMessageId = streamMeta.humanMessageId;
				trackingAttributes.hasSources = streamMeta.hasSources;
				trackingAttributes.citedProductList = streamMeta.citedProductList;
				trackingAttributes.sourceProducts = streamMeta.sourceProducts;
				trackingAttributes.messageActions = streamMeta.messageActions;
				trackingAttributes.messageDynamicUIData = streamMeta.messageDynamicUIData;
				trackingAttributes.invokedFrom = streamMeta.invokedFrom;
				trackingAttributes.scenarioId = streamMeta.scenarioId;
				trackingAttributes.sources = streamMeta.responseSources;
				trackingAttributes.usesJsm = streamMeta.usesJsm;
				// Keep Set type for compatibility with downstream consumers that call Array.from()
				trackingAttributes.pluginsInvoked = new Set(streamMeta.pluginsInvoked);
				trackingAttributes.skillsInvoked = streamMeta.skillsInvoked;
				trackingAttributes.executedStratusSkill = streamMeta.executedStratusSkill;

				const flatMinionOutputs = minionOrchestrationOutputs?.flat() ?? [];

				// Analytics: Verify if minion alias matches minion id
				minionVerificationAnalytics({
					minionAlias,
					agentId,
					minionsOutput: flatMinionOutputs,
					sendAnalyticsEvent,
				});

				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId,
				});
			}

			if (isStreamDynamicLayoutSubAgent(streamMessage) && fg('rovo_chat_enable_lumina')) {
				setMessages(
					conversationId,
					addSubAgentMessageToStoreAssistantMessage(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage,
					),
				);
			}

			if (isStreamAgenticUi(streamObj) && fg('jira_ai_rendering_a2ui')) {
				const normalizedAgenticUiMessage: StreamAgenticUi = {
					...streamObj.data,
					type: 'AGENTIC_UI',
				};
				setMessages(
					conversationId,
					addAgenticUiMessage(
						getMessages(conversationId),
						tmpAssistantMessageId,
						normalizedAgenticUiMessage,
					),
				);
			}

			if (isFollowUpMessage(streamMessage)) {
				hasReceivedFollowUp = true;
				const messages = getMessages(conversationId);
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				const augmentedFollowUp = appendCreatePageFollowUpIfNeeded(
					messages,
					streamMessage,
					productKey,
				);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: augmentedFollowUp,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
						humanMessageId: trackingAttributes.humanMessageId,
					},
				]);
			}

			/**
			 * Task status processing
			 */
			if (isStreamTaskStatus(streamMessage)) {
				onTaskStatusUpdate?.(streamMessage);
			}

			/**
			 * Todo snapshot processing
			 */
			if (isStreamTodoSnapshot(streamMessage)) {
				onTodoSnapshot?.({ todos: streamMessage.message.todos });
			}

			if (isStreamActionError(streamMessage)) {
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.key,
					errorMessage: streamMessage?.message?.data?.message,
					traceId,
				});
				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea: chatContextStoreActions.getIsStagingArea(),
								productKey,
							},
						},
						traceId: traceId,
					},
				);

				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: actionError,
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isDeepResearchEnabled: deepResearchEnabled,
						isThinkDeeperEnabled: thinkDeeperEnabled,
						isFastModeEnabled: fastModeEnabled,
						isWebSearchEnabled: webSearchEnabled,
						llmDebugLogsEnabled: storeUserMessages,
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
					},
				});

				experienceTracker.fail({
					name: ExperienceName.AIFC_MESSAGE,
					error: actionError,
					attributes: {
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
						llmDebugLogsEnabled: storeUserMessages,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						latestStreamMessageMillisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						latestStreamMessageType: latestStreamMessage?.type,
					},
				});

				if (fg('aifc-experience-timeout')) {
					const reason = streamActionInfo.isAction
						? 'action_error_while_executing_action'
						: 'action_error_without_classification';
					for (const name of [
						ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
						ExperienceName.AIFC_ALL_DATA_HANDLED,
					]) {
						experienceTracker.fail({
							name,
							error: actionError,
							attributes: {
								productKey,
								actionKey: streamActionInfo.actionKey,
								reason,
							},
						});
					}
				}

				throw actionError;
			}

			if (isStreamPromptRedaction(streamMessage)) {
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — the stream continues with the actual response after redaction.
					onPromptRedacted?.(streamMessage);
				}
			}

			if (isStreamPromptBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onPromptBlocked?.(streamMessage);
				}
			}

			if (isStreamResponseRedaction(streamMessage)) {
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — stream continues after response redaction.
					onResponseRedacted?.(streamMessage);
				}
			}

			if (isStreamResponseBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onResponseBlocked?.(streamMessage);
				}
			}

			if (isStreamAttachmentBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onAttachmentBlocked?.(streamMessage);
				}
			}

			if (isStreamError(streamMessage)) {
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.message_template,
					errorMessage: streamMessage?.message?.content,
					traceId,
				});
				// If a guard event (PROMPT_REDACTION, PROMPT_BLOCKED, RESPONSE_BLOCKED, or ATTACHMENT_BLOCKED) was
				// already received, the backend always follows with an ERROR to terminate the stream.
				// Treat it as a normal end of the guard flow — break without throwing.
				if (guardEventReceived) {
					break;
				}

				const { message, metadata, millisOffset, type } = streamMessage;

				// AUP violations are still considered successful
				if (isStreamAUPError(streamMessage)) {
					experienceTracker.succeed({
						name: sendMessageExperienceNameCompiled,
					});
					experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
				}

				sendMessageLatencyMetrics.cancel();
				deadAirTracker?.cancel();
				viewDeadAirBroker?.finish('errored');
				cancelStreamingMetrics();

				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId: traceId,
				});
			}
		}
	} catch (error: unknown) {
		/* for await catch block */
		const err = error instanceof Error ? error : new Error(String(error));

		if (err instanceof StreamErrorException) {
			throw err;
		}

		// this is for any errors that happening during processing the stream
		// e.g. BE terminates streaming suddenly
		sendMessageLatencyMetrics.cancel();
		deadAirTracker?.cancel();
		viewDeadAirBroker?.finish('aborted');

		cancelStreamingMetrics();

		if (isClientSideStreamingError(err)) {
			const reconnectionPossible = agentMessageId && isReconnectSupported;

			if (
				reconnectionPossible &&
				!FeatureGates.getExperimentValue('rovo_chat_reconnect_all_experiment', 'isEnabled', false)
			) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageId({
						chat: getMessages(conversationId),
						oldId: tmpAssistantMessageId,
						newId: agentMessageId!,
					}),
				);

				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId!,
				});

				throw new StreamDisconnectedError(err.message ?? 'Stream client side error', {
					agentMessageId: agentMessageId!,
					conversationId,
					answerIndex,
					lastEventId,
					originalError: err,
					traceId,
					detail: {
						millisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						type: latestStreamMessage?.type ?? '',
					},
				});
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'failed',
				actionSubject: 'rovoChatStream',
				attributes: {
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					isReconnectable: reconnectionPossible,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
					error: err.message,
				},
			});
		}

		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		// When reconnect attempt fails
		if (!deepResearchEnabled && fg('aifc-experience-timeout')) {
			const reason = streamActionInfo.isAction
				? 'stream_error_during_action'
				: 'stream_error_before_classification';
			for (const name of [
				ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
				ExperienceName.AIFC_ALL_DATA_HANDLED,
			]) {
				experienceTracker.fail({
					name,
					error: err,
					attributes: {
						productKey,
						actionKey: streamActionInfo.actionKey,
						reason,
					},
				});
			}
		}

		throw new StreamGeneratorError(
			err.message ?? 'Stream generator error',
			{
				traceId: traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			err,
		);
	} finally {
		inactivity?.stop();
		deadAirTracker?.complete();
		viewDeadAirBroker?.finish('completed');
	}

	/**********************************************************
	 *
	 * Stream processing END
	 *
	 **********************************************************/

	if (hasReceivedActionClassification && !hasReceivedActionComplete && hasReceivedFinalResponse) {
		// In the case ACTION_COMPLETE was never sent, it's likely that `AdfEditorMinion` early-exits with "no operations returned by LLM". So we abort the experiences rather than letting them timeout. This can potentially happen when LLM needs clarification.
		abortGenerateContentChildExperiences(
			experienceTracker,
			'stream_completed_without_action_complete',
			{
				actionKey: streamActionInfo.actionKey,
				productKey,
			},
		);
	}

	if (!hasReceivedFollowUp && hasReceivedFinalResponse) {
		const messages = getMessages(conversationId);
		if (shouldAppendCreatePageFollowUp(messages, productKey)) {
			setMessages(conversationId, [
				...messages,
				{
					type: 'FOLLOW_UP',
					content: createSyntheticCreatePageFollowUp(),
					id: randomIntFromInterval(0, 1000000),
					agentMessageId: getLastAgentMessageID(messages),
					humanMessageId: trackingAttributes.humanMessageId,
				},
			]);
		}
	}

	updateCurrentConversation({
		sourceAgentId: getSourceAgentId(
			getMessages(conversationId),
			getConversation(getState(), conversationId),
		),
	});

	return {
		hasReceivedFinalResponse,
		hasLoggedInitiatedEvent,
		hasAbortedForImplicitThinkDeeper,
		implicitThinkDeeperSendMessageMetrics,
		implicitThinkDeeperExperienceName,
		guardEventReceived,
	};
};

type StreamActionInfo = {
	isAction: boolean;
	actionKey?: string;
};

/**
 * Compute the per-send routing identity for a send.
 *
 * Wraps `deriveAgentSendRequestIdentity` with the shared inputs (default agent,
 * explicit caller agent id, and the draft-only-on-chatInput override rule) so the
 * outer early-handler fallback and the inner per-send path stay in lockstep.
 *
 * The no-override wire/routing fallback follows the CALLER's agent intent
 * (`conversationAgentForRequest`), not necessarily the stored conversation agent:
 * in-sidebar sends pass the conversation's own agent, a cross-surface pass-over
 * passes default Rovo. This keeps author and wire consistent for the turn and
 * matches gate-off. See ai-mate AGENTS.md "Rovo Chat agent identity, routing, and
 * display".
 */
const deriveSendRoutingIdentity = ({
	conversationAgentForRequest,
	agentIdParam,
	inputSelectedAgentIds,
	messageSources,
	omitConversationDefaultAgentRequestFields,
}: {
	conversationAgentForRequest: { id: string; named_id?: string };
	agentIdParam?: string;
	inputSelectedAgentIds?: string[];
	messageSources?: Partial<MessageSource>;
	omitConversationDefaultAgentRequestFields?: boolean;
}) =>
	deriveAgentSendRequestIdentity({
		conversationAgent: conversationAgentForRequest,
		defaultAgent: DEFAULT_AGENT,
		explicitAgentId: agentIdParam,
		omitConversationDefaultAgentRequestFields,
		inputSelectedAgentIds,
		// The unsent agent-selector draft only routes when the send originates with
		// `messageSources.interactionSource === 'chatInput'`; cross-surface handoffs
		// (e.g. the Confluence inline Rovo input) must not inherit the sidebar's draft.
		allowSelectedAgentOverride: messageSources?.interactionSource === 'chatInput',
	});

/**
 * Build the optimistic `conversation.agent` metadata used when a send creates the
 * conversation. Prefers the already-stored conversation agent, then the caller's
 * rich agent metadata when it matches the resolved conversation agent id, then a
 * provisional placeholder built from that id (the header hydrates the rest).
 */
const buildOptimisticConversationAgent = ({
	agent,
	currentConversation,
	conversationAgentId,
}: {
	agent: Agent;
	currentConversation: Conversation | undefined;
	conversationAgentId: string;
}): AgentMetadata | undefined => {
	const callerAgentMatchesConversation = agent.id === conversationAgentId;
	const callerAgentConversationMetadata: AgentMetadata | undefined = callerAgentMatchesConversation
		? {
				agent_id: agent.id,
				agent_named_id: agent.external_config_reference ?? agent.named_id,
				agent_name: agent.name,
				creator_type: agent.creator_type,
				identity_account_id: agent.identity_account_id ?? undefined,
				is_default: agent.is_default,
				forge_url: agent.icon ?? undefined,
			}
		: undefined;
	return (
		currentConversation?.agent ??
		callerAgentConversationMetadata ??
		createProvisionalConversationAgent(conversationAgentId)
	);
};

export const sendMessageActionProvider: (params: SendMessageParams) => ChatStoreAction =
	({
		prompt,
		/*
As of RAGE-60 the default agent is no longer required to be sent to the backend, however
it is still required for other local logic
TODO: https://product-fabric.atlassian.net/browse/RAGE-418
*/
		agent = DEFAULT_AGENT,
		agentId: agentIdParam,
		conversationId: conversationIdParam,
		storeUserMessages,
		messageSources,
		files,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		appFilters,
		addCurrentPageAsContextUserPreference,
		productKey,
		source,
		promptStreamHook,
		creationContextParams,
		additionalAnalyticAttributes,
		contentContext,
		minionAlias,
		onTempMessageIdReplacedOverride,
		contentId,
		openChat,
		openChatOnError,
		agentVersion,
		spaceId,
		searchArtifact,
		skillSelectionSource,
		skipEditorContext,
		shouldExtractConversationScopes,
		parentMessageId,
		editRollback,
		setConversationData,
		createConversationOptimistically,
		addHumanMessage,
		addAssistantMessage,
		clearFollowUpMessages,
		setSelectedConversation,
		reconnectToInProgressStream,
	}) =>
	async (
		{ dispatch, getState, setState },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			chatContextStoreActions,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			markIncompleteResearchesErrored,
			updateResearchMessageId,
			clearMessageResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			getChatInitiator,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamError,
			processMessageStreamActionClassification,
			broadcastStreamStatus,
			onNewConversationStartingWithSendMessage,
			onTempMessageIdReplaced: containerOnTempMessageIdReplaced,
			browserContextSanitizer,
			viewDeadAirBroker,
			getIsChatMounted,
			capabilities,
			unifiedTasksActions,
		},
	) => {
		const initialConversationForRouting = getConversation(
			getState(),
			conversationIdParam ?? getState().currentConversationId,
		);
		const conversationAgentIdForRoutingFallback = resolveConversationAgentIdForRouting({
			conversation: initialConversationForRouting,
			fallbackAgentId: agent.id,
		});
		// `sourceAgentId` for the temporary local conversation; per-turn routing is computed by `deriveSendRoutingIdentity`.
		const sourceAgentIdForTemporaryLocalConversation = agentIdParam ?? agent.id;

		// Early error/success handlers can run before the inner send path recomputes
		// request routing and updates `activeSendMessageExperienceName`. Start with
		// the best synchronous routing identity so those handlers classify analytics
		// consistently if the send exits early.
		const { routingAgentId: fallbackRoutingAgentId } = deriveSendRoutingIdentity({
			conversationAgentForRequest: {
				id: conversationAgentIdForRoutingFallback,
				named_id: agent.named_id,
			},
			agentIdParam,
			inputSelectedAgentIds: initialConversationForRouting?.selectedAgentIds,
			messageSources,
		});
		let activeSendMessageExperienceName: ReturnType<typeof sendMessageExperienceName> =
			sendMessageExperienceName({
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				isCustomRovoAgent: isRovoCustomAgent(fallbackRoutingAgentId),
			});
		const { messageType } = getOptimisticMessageTypes(deepResearchEnabled, webSearchEnabled);

		setLastMessageSentAt(Date.now());

		const getActiveSendMessageExperienceName = () => activeSendMessageExperienceName;

		let pageContext: string | undefined;

		// Snapshot captured right before the optimistic add when `editRollback` is set,
		// so that a terminal failure can restore the conversation to its pre-edit state
		// by slicing back to this length and re-appending `editRollback.restoreMessages`.
		let rollbackSnapshot: { conversationId: string; lengthBeforeOptimistic: number } | undefined;

		const performEditRollback = () => {
			if (!editRollback || !rollbackSnapshot) {
				return;
			}
			const { conversationId: rollbackConvId, lengthBeforeOptimistic } = rollbackSnapshot;
			const current = getMessages(rollbackConvId);
			setMessages(rollbackConvId, [
				...current.slice(0, lengthBeforeOptimistic),
				...editRollback.restoreMessages,
			]);
		};

		const sendMessageFn = async ({ conversationId }: { conversationId: string }) => {
			const currentConversation = getConversation(getState(), conversationId);
			const currentConversationAgentId = resolveConversationAgentIdForRouting({
				conversation: currentConversation,
				fallbackAgentId: conversationAgentIdForRoutingFallback,
			});
			const conversationAgentForOptimisticCreation = buildOptimisticConversationAgent({
				agent,
				currentConversation,
				conversationAgentId: currentConversationAgentId,
			});
			const { routedSelectedAgentId, routingAgentId, agentIdForRequest, agentNamedIdForRequest } =
				deriveSendRoutingIdentity({
					conversationAgentForRequest: { id: agent.id, named_id: agent.named_id },
					agentIdParam,
					inputSelectedAgentIds: currentConversation?.selectedAgentIds,
					messageSources,
					omitConversationDefaultAgentRequestFields: true,
				});
			const sendMessageExperienceNameCompiled = sendMessageExperienceName({
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				isCustomRovoAgent: isRovoCustomAgent(routingAgentId),
			});
			activeSendMessageExperienceName = sendMessageExperienceNameCompiled;
			const resolvedAgentVersion = agentVersion ?? currentConversation?.agentVersion;

			if (currentConversation?.storeAbortController) {
				// Silently aborting if there is another operation in progress
				return;
			}

			const tmpHumanMessageId = generateId();

			const tmpAssistantMessageId = generateTmpMessageId(conversationId);

			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;

			const isFirstPrompt = isEmptyConversation(conversationId);

			const contentContextFromStore = chatContextStoreActions.getIsStagingArea()
				? 'staging-area'
				: 'global';
			const contentContextValue = contentContext ?? contentContextFromStore;

			let trackingAttributes: SendMessageTrackingAttributes = getInitialTrackingAttributes(
				routingAgentId,
				tmpAssistantMessageId,
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				taskModeEnabled,
				webSearchEnabled,
				messageSources,
				isFirstPrompt,
				prompt,
				files,
				appFilters,
				{
					templateId: creationContextParams?.templateInput?.templateId,
					templateType: creationContextParams?.templateInput?.templateType,
				},
				creationContextParams?.forcedContentType,
			);

			// Prepare fields for the temporary local conversation created before the backend returns one.
			const conversationAgentForTemporaryLocalConversation = fg('rovo_chat_agent_selection')
				? conversationAgentForOptimisticCreation
				: undefined;
			const conversationAgentFieldsForTemporaryLocalConversation =
				conversationAgentForTemporaryLocalConversation
					? { agent: conversationAgentForTemporaryLocalConversation }
					: {};

			if (isFirstPrompt) {
				conversationId = crypto.randomUUID();
				onNewConversationStartingWithSendMessage(conversationId, {
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					webSearchEnabled,
					useCurrentPageContext: addCurrentPageAsContextUserPreference,
					appFilters,
				});
				// Create the conversation with its initial identity; first-turn routing may
				// refine `conversation.agent` below.
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						status: CHAT_STORE_STATUS.awaitingResponse,
						sourceAgentId:
							currentConversation?.sourceAgentId ?? sourceAgentIdForTemporaryLocalConversation,
						lastMessageType: messageType,
						lastAgentMessageStatus: 'IN_PROGRESS',
						selectedAgentIds: currentConversation?.selectedAgentIds
							? [...currentConversation.selectedAgentIds]
							: undefined,
						// Immediately scope the optimistic conversation to the space so it
						// appears under the space in the sidenav rather than the regular list.
						...(spaceId && fg('rovo_chat_spaces_fe') ? { rovoSpaceId: spaceId } : {}),
						...(resolvedAgentVersion && fg('rovo_agent_versioning_enabled')
							? { agentVersion: resolvedAgentVersion }
							: {}),
						...conversationAgentFieldsForTemporaryLocalConversation,
					}),
				);

				// First-turn routing can refine display identity; keep `sourceAgentId` as history.
				const normalizedRoutingAgentId = routingAgentId || undefined;
				const routedConversationAgent =
					createProvisionalConversationAgent(normalizedRoutingAgentId);
				if (
					fg('rovo_chat_agent_selection') &&
					routedConversationAgent &&
					normalizedRoutingAgentId !== currentConversationAgentId
				) {
					dispatch(
						setConversationData({
							conversationId,
							data: { agent: routedConversationAgent },
						}),
					);
				}
			}

			const {
				sendMessageLatencyMetrics,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToActionCompletion,
				cancelStreamingMetrics,
				metricsStartTime,
			} = initLatencyMetrics({
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				agentId: routingAgentId,
			});

			experienceTracker.start({
				name: sendMessageExperienceNameCompiled,
				id: conversationId,
				attributes: {
					conversationId,
					interactionSource: messageSources?.interactionSource,
					featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				},
			});

			const chatInitiator = getChatInitiator();

			const { isRemixFollowUp, lastAssistantMessageDynamicUiType } = (() => {
				const lastAction = findLastAssistantAction(getMessages(conversationId), () => true);
				if (!lastAction || !isRemixGeneratedFlow(lastAction)) {
					return { isRemixFollowUp: false, lastAssistantMessageDynamicUiType: undefined };
				}
				// BE stores dynamicUiType under `contentType` in action data
				const contentType = (lastAction.data as { contentType?: string } | undefined)?.contentType;
				return {
					isRemixFollowUp: true,
					lastAssistantMessageDynamicUiType: contentType,
				};
			})();

			let sendMessageContext: Awaited<ReturnType<typeof populateSendMessageContext>>;
			try {
				sendMessageContext = await populateSendMessageContext(
					chatContextStoreActions,
					isFirstPrompt,
					source,
					addCurrentPageAsContextUserPreference,
					contentContextValue,
					creationContextParams,
					productKey,
					sendAnalyticsEvent,
					contentId,
					lastAssistantMessageDynamicUiType,
					skipEditorContext,
					isRemixFollowUp,
					searchArtifact,
				);
			} catch (error: unknown) {
				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: error as Error,
					attributes: {
						deepResearchEnabled,
						fastModeEnabled,
						thinkDeeperEnabled,
						webSearchEnabled,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						failureSource: 'populateSendMessageContext',
					},
				});

				// Note: We don't track error in error event as discussed here:
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/341408/overview#comment-2004687398
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				return;
			}

			// spaceId comes from the emptyChatLaunch projectContext (e.g. rovo-for-you space card chat button).
			const resolvedSpaceId =
				spaceId ??
				(sendMessageContext.aiFeatureContext?.projectContext as { spaceId?: string } | undefined)
					?.spaceId;

			const storeState = chatContextStoreActions.getAllContext();
			trackingAttributes.contextProvided = Object.entries(storeState).reduce(
				(acc, [key, value]) => {
					acc[key as keyof ChatContextStore] = Boolean(value);
					return acc;
				},
				{} as Record<keyof ChatContextStore, boolean>,
			);
			trackingAttributes.contextProvided.editorContext = Boolean(sendMessageContext.editorContext);
			trackingAttributes.contextProvided.whiteboardContext = Boolean(
				sendMessageContext.whiteboardContext,
			);
			trackingAttributes.contextProvided.slidesContext = Boolean(sendMessageContext.slidesContext);
			trackingAttributes.contextProvided.databaseContext = Boolean(
				sendMessageContext.databaseContext,
			);
			trackingAttributes.contextProvided.browserContext = Boolean(
				sendMessageContext.browserContext,
			);
			trackingAttributes.contextProvided.dashboardContext = Boolean(
				sendMessageContext.dashboardContext,
			);
			trackingAttributes.contextProvided.aiFeatureContext = Boolean(
				sendMessageContext.aiFeatureContext,
			);
			trackingAttributes.contextProvided.jiraWorkflowContext = Boolean(
				sendMessageContext.jiraWorkflowContext,
			);
			trackingAttributes.contextProvided.domContext = Boolean(
				sendMessageContext.browserContext?.context?.htmlBody,
			);

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'sent',
				actionSubject: 'message',
				attributes: {
					timestamp: undefined,
					messageId: trackingAttributes.humanMessageId,
					interactionSource: trackingAttributes.interactionSource,
					followUpSource: trackingAttributes.followUpSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					maxModeEnabled: trackingAttributes.taskModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					contextProvided: trackingAttributes.contextProvided,
					initiator: chatInitiator,
					isFirstPrompt: trackingAttributes.isFirstPrompt,
					promptFeatures: trackingAttributes.promptFeatures,
					fileCount: trackingAttributes.fileCount,
					appFilters: trackingAttributes.appFilters,
					appFilterCount: trackingAttributes.appFilterCount,
					has3pApps: trackingAttributes.appFilters?.some((app) => is3pApp(app.friendlyName)),
					template: trackingAttributes.template,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					conversationChannelId: conversationId,
				},
			});

			if (editRollback && !rollbackSnapshot) {
				rollbackSnapshot = {
					conversationId,
					lengthBeforeOptimistic: getMessages(conversationId).length,
				};
			}

			const { abortController, confirmAbort, updateCurrentConversation } =
				hydrateStoreWithNewMessage(
					dispatch,
					conversationId,
					messageType,
					tmpHumanMessageId,
					tmpAssistantMessageId,
					prompt,
					files,
					sourceAgentIdForTemporaryLocalConversation,
					setConversationData,
					addHumanMessage,
					addAssistantMessage,
					clearFollowUpMessages,
					routedSelectedAgentId,
				);

			// Assumed the insert is successful and inserts at the last position
			const answerIndex = getMessages(conversationId).length - 1;

			// Broadcast stream status to content targets for state sharing
			const broadcastStreamLifecycle = createBroadcastStreamLifecycle({
				broadcastStreamStatus,
				chatContextStoreActions,
				contentContextValue,
				targetId: contentId,
				assistantMessageId: tmpAssistantMessageId,
			});

			// Initialize action tracking
			let streamActionInfo: StreamActionInfo = {
				isAction: false,
			};

			// Create message stream
			try {
				// Derive page context for analytics from the available context objects
				pageContext = getPageContext({
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					databaseContext: sendMessageContext.databaseContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					browserUrl: sendMessageContext.browserContext?.context?.browserUrl,
				});
				trackingAttributes.pageContext = pageContext;

				trackingAttributes.viewContext = getViewContext({
					aiFeatureContext: sendMessageContext.aiFeatureContext,
				});

				trackingAttributes.invokedFrom = getInvokedFrom({
					messageActionKey: streamActionInfo?.actionKey,
				});

				// Check if the Jira refresh experiment is enabled but default is set to `true` to pass all non-Jira products

				// Omit caller source controls only for sends routed by `routedSelectedAgentId`;
				// see `getSourceControlValueForBackend`.
				const webSearchEnabledForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: webSearchEnabled,
				});
				const appFiltersForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: appFilters,
				});
				const agentNamedIdForBackend = fg('rovo_chat_agent_selection')
					? agentNamedIdForRequest
					: undefined;

				const result = await assistanceService.sendMessageStream({
					message: prompt,
					agentId: agentIdForRequest,
					// Gate-off must not introduce `recipient_agent_named_id` on the wire.
					agentNamedId: agentNamedIdForBackend,
					controller: abortController,
					conversationId,
					storeMessage: storeUserMessages,
					citationsEnabled: true,
					fileIds: files?.map((file) => file.id),
					deepResearchEnabled,
					thinkDeeperEnabled,
					planModeEnabled: taskModeEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					webSearchEnabled: webSearchEnabledForBackend,
					appFilters: appFiltersForBackend?.map((app) => app.source),
					browserContext: sendMessageContext.browserContext,
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					slidesContext: sendMessageContext.slidesContext,
					databaseContext: sendMessageContext.databaseContext,
					additionalContext: sendMessageContext.additionalContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					jiraWorkflowContext: sendMessageContext.jiraWorkflowContext,
					dashboardContext: sendMessageContext.dashboardContext,
					dashboardEditorContext: sendMessageContext.dashboardEditorContext,
					useCurrentBrowsingContext: sendMessageContext.useCurrentBrowsingContext,
					browserContextSanitizer,
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: true,
					optInAnsNow: true,
					optInNewToolTraces: FeatureGates.getExperimentValue(
						'rovo_chat_thinking_traces_improvements',
						'isEnabled',
						false,
						{ fireExperimentExposure: false },
					),
					optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
					optInSSEStream: true,
					...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
					...(resolvedSpaceId ? { spaceId: resolvedSpaceId } : {}),
					capabilities,
					shouldExtractConversationScopes,
					parentMessageId,
				});
				generator = result.generator;
				responseData = result.responseData;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (fg('aifc_show_error_ui_for_inline_rovo')) {
					promptStreamHook?.onError?.(error, tmpAssistantMessageId, {
						openChatOnError,
						conversationId,
					});
				}
				performEditRollback();
				handleSendMessageServiceCallFailure(
					error,
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					webSearchEnabled,
					streamActionInfo,
					chatContextStoreActions,
					experienceTracker,
					sendMessageExperienceNameCompiled,
					trackingAttributes,
					updateCurrentConversation,
					sendMessageLatencyMetrics,
					cancelStreamingMetrics,
					broadcastStreamLifecycle,
					confirmAbort,
					trackAIMAUEvent,
					messageSources,
					abortController,
					hasLoggedInitiatedEvent,
					tmpAssistantMessageId,
					clearMessageResearch,
					getState,
					conversationId,
					storeUserMessages,
				);
				return;
			}

			const {
				hasReceivedFinalResponse,
				hasLoggedInitiatedEvent: hasLoggedInitiatedEventForStream,
				hasAbortedForImplicitThinkDeeper,
				implicitThinkDeeperSendMessageMetrics,
				implicitThinkDeeperExperienceName,
				guardEventReceived: wasGuardTerminated,
			} = await processStream({
				prompt,
				generator,
				productKey,
				answerIndex,
				tmpHumanMessageId,
				tmpAssistantMessageId,
				thinkDeeperEnabled,
				fastModeEnabled,
				deepResearchEnabled,
				webSearchEnabled,
				sendMessageExperienceNameCompiled,
				traceId: responseData?.traceId,
				updateCurrentConversation,
				trackingAttributes,
				conversationId,
				getState,
				promptStreamHook,
				openChat,
				openChatOnError,
				experienceTracker,
				setMessages,
				getMessages,
				processStreamResearch,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamError,
				processMessageStreamActionClassification,
				containerOnTempMessageIdReplaced,
				updateResearchMessageId,
				sendAnalyticsEvent,
				chatContextStoreActions,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToActionCompletion,
				broadcastStreamLifecycle,
				sendMessageLatencyMetrics,
				cancelStreamingMetrics,
				onTempMessageIdReplacedOverride,
				streamActionInfo,
				trackAIMAUEvent,
				messageSources,
				additionalAnalyticAttributes,
				hasLoggedInitiatedEvent,
				clearMessageResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				viewDeadAirBroker,
				creationContextParams,
				minionAlias: assistanceService.getConfig().minionAlias,
				agentId: routingAgentId,
				agentName: agent?.name,
				agentIdentityAccountId: agent?.identity_account_id,
				storeUserMessages,
				skillSelectionSource,
				assistanceService,
				abortController,
				getIsChatMounted,
				metricsStartTime,
				onTaskStatusUpdate: (streamMessage) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					handleStreamTaskStatus({
						conversationId,
						taskMessage: streamMessage.message,
						messageId: trackingAttributes.messageId,
						target: buildUnifiedStreamTaskTarget(unifiedTasksActions),
					});
				},
				onTodoSnapshot: ({ todos }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.setTaskTodoSnapshot({
						conversationId,
						todoSnapshot: { todos },
					});
				},
				onPromptBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onPromptRedacted: (streamMessage) => {
					// Only update the human message with the redacted prompt.
					// Keep the assistant placeholder — the stream continues with the actual response.
					const chat = getMessages(conversationId);
					let lastHumanIdx = -1;
					chat.forEach((m, i) => {
						if (isStoreHuman(m)) {
							lastHumanIdx = i;
						}
					});
					if (lastHumanIdx === -1) {
						return;
					}
					const newChat = [...chat];
					newChat[lastHumanIdx] = {
						...(chat[lastHumanIdx] as StoreHuman),
						redactedPrompt: streamMessage.message.redacted_prompt,
					};
					setMessages(conversationId, newChat);
				},
				onResponseRedacted: (streamMessage) => {
					// Set redactedResponse on the latest assistant message so the scribble UI renders.
					// Do NOT set guardEventReceived — the stream continues after response redaction.
					const chat = getMessages(conversationId);
					let lastAssistantIdx = -1;
					chat.forEach((m, i) => {
						if (isStoreAssistant(m)) {
							lastAssistantIdx = i;
						}
					});
					if (lastAssistantIdx === -1) {
						return;
					}
					const newChat = [...chat];
					newChat[lastAssistantIdx] = {
						...(chat[lastAssistantIdx] as StoreAssistant),
						redactedResponse: streamMessage.message.redacted_response,
					};
					setMessages(conversationId, newChat);
				},
				onResponseBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onAttachmentBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
			});

			hasLoggedInitiatedEvent = hasLoggedInitiatedEventForStream;

			// TODO: This should be behind the !aborted logic, but due to a bug in the BE service, aborted requests
			// will client connection with the server yet still complete in the background. This results in the UI showing
			// no message updates, until reload when the actual resolved message can be sesen.
			// Once that is fixed, we can place this behind the !aborted logic.
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'received',
				actionSubject: 'agentMessage',
				attributes: {
					// messageId must remain the ID received from the server in the FINAL_RESPONSE
					// in order to map correctly to our event funnel, e.g `agentMessage received` -> `agentResponse content copied`
					messageId: trackingAttributes.messageId,
					humanMessageId: trackingAttributes.humanMessageId,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					requestId: trackingAttributes.requestId,
					hasSources: trackingAttributes.hasSources,
					citedProductList: trackingAttributes.citedProductList,
					sourceProducts: trackingAttributes.sourceProducts,
					has3pApps: Object.keys(trackingAttributes.sourceProducts ?? {}).some(is3pApp),
					timestamp: new Date().toISOString(),
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					initiator: chatInitiator,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					scenarioId: trackingAttributes.scenarioId,
				},
			});

			const wasProgrammaticallyAborted = abortController.signal.aborted;
			updateCurrentConversation(
				clearStatus({
					lastAgentMessageStatus: wasProgrammaticallyAborted ? 'CANCELLED' : 'SUCCESSFUL',
				}),
			);

			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));

			if (abortController.signal.aborted) {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
						},
					});
				}

				trackAIMAUEvent({
					type: 'dismissed',
					userGenerated: true,
					messageId: trackingAttributes.messageId,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					subjectId: trackingAttributes.interactionSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
					},
				});

				confirmAbort();
				broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

				// Call the cancel API if we have a message ID
				if (trackingAttributes.messageId) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						.catch((error: any) => {
							// Log but don't throw - the abort is already happening
							logException({
								name: 'rovoCancelMessageStream',
								exception: error,
								tags: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}

				// For implicit think-deeper, the auto-mode experience was already aborted inside processStream;
				// abort the re-attributed think-deeper experience instead.
				experienceTracker.abort({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
					reason: 'Aborted',
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isProgrammaticAbort: true, // always true because we're within abortController.signal.aborted (further explained in similar abort for AbortError case)
					},
				});
				experienceTracker.abort({
					name: ExperienceName.AIFC_MESSAGE,
					reason: 'stream aborted',
				});
				// Always mark incomplete researches as errored on abort — the backend can
				// autonomously trigger think-deeper even in auto mode
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				// Clear research data to remove lingering think deeper state
				clearMessageResearch({ messageId: tmpAssistantMessageId });
				return;
			}

			// Only fire if the request wasn't aborted
			else {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
						},
					});
				}

				//If we're done processing the stream and no answer part has been processed, then it means that the BE is hanging. Throw an error
				if (!hasReceivedFinalResponse && !wasGuardTerminated) {
					const agentDetails = await assistanceService.getAgentDetails(routingAgentId);
					if (agentDetails.deactivated) {
						// If agent is deactivated, throw correct error
						const agentError = new NoFinalResponseError(
							'The selected agent may no longer be available.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: agentError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
						return;
					} else {
						// Agent is active, BE hanging
						const timeoutError = new NoFinalResponseError(
							'The response took too long and timed out. Try refreshing the page.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: timeoutError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

						throw timeoutError;
					}
				}

				/**
				 * IMPORTANT: CRITICAL ANALYTICS EVENT
				 *
				 * This event is essential for business metrics and monitoring.
				 *
				 * DO NOT MODIFY the structure, content, or emission logic of this event without:
				 *   - Careful review of potential impacts on data quality, privacy, and business reporting.
				 *   - Explicit approval from both analytics and security stakeholders.
				 *
				 * Reference: Incident HOT-120175 (UGC page title and URLs leak)
				 * Event Registry: https://data-portal.internal.atlassian.com/analytics/registry/68936?tabIndex=Details
				 */
				const dynamicUiType =
					creationContextParams?.dynamicUiType ?? sendMessageContext?.editorContext?.dynamicUiType;
				trackAIMAUEvent({
					type: 'viewed',
					subjectId: messageSources?.followUpSource,
					messageId: trackingAttributes.messageId,
					source: messageSources?.interactionSource,
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					invokedFrom: trackingAttributes.invokedFrom,
					proactive: false,
					userGenerated: true,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					timestamp: new Date().toISOString(),
					citedProductList: trackingAttributes.citedProductList,
					sourceProducts: trackingAttributes.sourceProducts,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					skillsInvoked: trackingAttributes.skillsInvoked,
					executedStratusSkill: trackingAttributes.executedStratusSkill,
					responseSources: trackingAttributes.sources,
					usesJsm: trackingAttributes.usesJsm,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					conversationChannelId: conversationId,
					scenarioId: trackingAttributes.scenarioId,
					pageContext: trackingAttributes.pageContext,
					attributes: {
						...interactionModeExecutedAttributes(trackingAttributes.interactionModeExecuted),
						...(dynamicUiType && { dynamicUiType }),
						...(creationContextParams?.dynamicUiSubtype && {
							dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
						}),
						isTabVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
						...(creationContextParams?.dynamicUiSource && {
							remixNodeTypes: trackingAttributes.remixNodeTypes,
							...(trackingAttributes.remixWordCount !== undefined && {
								remixWordCount: trackingAttributes.remixWordCount,
							}),
						}),
						...(trackingAttributes.aiFeatureName && {
							aiFeatureName: trackingAttributes.aiFeatureName,
						}),
					},
				});

				// Experience is aborted in `abortRequests`
				// For implicit think-deeper, succeed the re-attributed think-deeper reliability SLO instead
				experienceTracker.succeed({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
				});

				// Broadcast that the stream has completed successfully
				broadcastStreamLifecycle(StreamLifecycleState.COMPLETED);

				if (fg('rovo_chat_chat_history_refresh')) {
					//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
					if (getState().currentConversationId !== conversationId) {
						dispatch(
							setConversationData({
								conversationId,
								data: { readStatus: false },
							}),
						);
					}
				}
			}

			// For implicit think-deeper, stop the re-attributed think-deeper metric instead
			if (!hasAbortedForImplicitThinkDeeper) {
				sendMessageLatencyMetrics.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			} else {
				implicitThinkDeeperSendMessageMetrics?.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			}
		};

		const conversationId = conversationIdParam ?? getState().currentConversationId;

		try {
			await sendMessageFn({ conversationId });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// Legacy reconnection path: handle StreamDisconnectedError thrown by processStream
			// when rovo_chat_reconnect_all_experiment is OFF.
			if (error instanceof StreamDisconnectedError) {
				const reconnectionOnComplete = (result: ReconnectCompletionResult) => {
					if (result.success) {
						const meta = result.streamMeta;
						trackAIMAUEvent({
							type: 'viewed',
							subjectId: messageSources?.followUpSource,
							messageId: error.agentMessageId,
							source: messageSources?.interactionSource,
							messageActions: meta?.messageActions,
							proactive: false,
							userGenerated: true,
							singleInstrumentationID: error.agentMessageId,
							timestamp: new Date().toISOString(),
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
							webSearchEnabled,
							conversationChannelId: error.conversationId,
							citedProductList: meta?.citedProductList ?? {},
							sourceProducts: meta?.sourceProducts ?? {},
							pluginsInvoked: meta?.pluginsInvoked,
							skillsInvoked: meta?.skillsInvoked,
							responseSources: meta?.responseSources,
							usesJsm: meta?.usesJsm,
							scenarioId: meta?.scenarioId,
							pageContext,
							attributes: {
								...interactionModeExecutedAttributes(meta?.interactionModeExecuted),
								...(messageSources?.aiFeatureName && {
									aiFeatureName: messageSources.aiFeatureName,
								}),
							},
						});
						experienceTracker.succeed({
							name: getActiveSendMessageExperienceName(),
						});
					} else {
						sendAnalyticsEvent({
							eventType: 'track',
							action: 'failed',
							actionSubject: 'rovoChatStream',
							attributes: {
								isDeepResearchEnabled: deepResearchEnabled,
								isThinkDeeperEnabled: thinkDeeperEnabled,
								isWebSearchEnabled: webSearchEnabled,
								isAIFCStreaminAction: false,
								isReconnectable: true,
								conversationId,
								instrumentationId: error.agentMessageId,
								requestId: error.agentMessageId,
								interactionSource: messageSources?.interactionSource,
								contentContext: chatContextStoreActions.getIsStagingArea()
									? 'staging-area'
									: 'global',
								userAgent: navigator?.userAgent,
								error: error.message,
							},
						});
						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: error.agentMessageId,
							singleInstrumentationID: error.agentMessageId,
							timestamp: new Date().toISOString(),
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
							webSearchEnabled,
							attributes: {
								...interactionModeRequestedAttributes(
									getInteractionModeRequested({
										deepResearchEnabled,
										thinkDeeperEnabled,
										fastModeEnabled,
										taskModeEnabled,
									}),
								),
							},
						});
						experienceTracker.fail({
							name: getActiveSendMessageExperienceName(),
							error: result.error ?? new Error('Reconnection failed'),
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});
					}
				};

				await dispatch(
					reconnectToInProgressStream({
						conversationId: error.conversationId,
						messageId: error.agentMessageId,
						answerIndex: error.answerIndex,
						isDeepResearch: deepResearchEnabled,
						lastEventId: error.lastEventId,
						abortCurrentRequest: true,
						onComplete: reconnectionOnComplete,
					}),
				);

				return;
			}

			if (isChannelNotFoundError(error)) {
				const createdChannel = await assistanceService.createConversationChannel();

				setState({
					conversations: {
						...getState().conversations,
						[createdChannel.id]: {
							id: createdChannel.id,
							name: createdChannel.name,
							timeCreated: createdChannel.time_created,
							status: CHAT_STORE_STATUS.idle,
							traces: [],
							selectedAgentIds:
								createdChannel.agent && fg('rovo_chat_chat_history_refresh')
									? [createdChannel.agent.agent_id]
									: [],
							messages: [],
							...(createdChannel.agent && fg('rovo_chat_chat_history_refresh')
								? { agent: createdChannel.agent }
								: {}),
						},
					},
				});

				setMessages(createdChannel.id, []);

				dispatch(
					setSelectedConversation({
						conversationId: createdChannel.id,
					}),
				);

				await sendMessageFn({ conversationId: createdChannel.id });
			} else {
				performEditRollback();
				throw error;
			}
		}
	};

type InternalStreamStats = {
	totalChunks: number;
	traceChunks: number;
	answerChunks: number;
	actionChunks: number;
	researchChunks: number;
	otherChunks: number;
	firstChunkAt?: number;
	lastChunkAt?: number;
	sumInterChunkMs: number;
	pluginsObserved: Set<string>;
};

const getStreamStats = () => {
	const stats: InternalStreamStats = {
		totalChunks: 0,
		traceChunks: 0,
		answerChunks: 0,
		actionChunks: 0,
		researchChunks: 0,
		otherChunks: 0,
		firstChunkAt: undefined,
		lastChunkAt: undefined,
		sumInterChunkMs: 0,
		pluginsObserved: new Set<string>(),
	};

	const update = (streamMessage: StreamMessage) => {
		const now = performance.now();
		stats.totalChunks += 1;
		if (stats.firstChunkAt === undefined) {
			stats.firstChunkAt = now;
		}
		if (stats.lastChunkAt !== undefined) {
			stats.sumInterChunkMs += now - stats.lastChunkAt;
		}
		stats.lastChunkAt = now;

		if (isStreamTrace(streamMessage)) {
			stats.traceChunks += 1;
		} else if (isStreamAnswerPart(streamMessage)) {
			stats.answerChunks += 1;
		} else if (isStreamResearchPayload(streamMessage)) {
			stats.researchChunks += 1;
		} else if (isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) {
			// Count action-related chunks (prepare/part)
			stats.actionChunks += 1;
		} else {
			// Count unknown chunks
			stats.otherChunks += 1;
		}

		// Observe plugin names if present early (often only present on final response)
		const invocations = isStreamResponse(streamMessage)
			? streamMessage.message?.message?.plugin_invocations
			: [];
		if (Array.isArray(invocations)) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			invocations.forEach((p: any) => p?.plugin_name && stats.pluginsObserved.add(p.plugin_name));
		}
	};

	const getStats = () => {
		const avgGapMs =
			stats.totalChunks > 1 ? Math.round(stats.sumInterChunkMs / (stats.totalChunks - 1)) : 0;
		return {
			...stats,
			pluginsObserved: Array.from(stats.pluginsObserved),
			averageGapMs: avgGapMs,
		};
	};

	return { update, getStats };
};

export const createBetweenChunksInactivityLogger = (config: InactivityWatchdogConfig) => {
	let armed = false;
	let firstChunkArrived = false;
	let timer: number | undefined;
	let stallCount = 0;

	const clearTimer = () => {
		if (timer !== undefined) {
			clearTimeout(timer);
			timer = undefined;
		}
	};

	const schedule = (stage: 'newResponse' | 'firstGap' | 'betweenChunks') => {
		clearTimer();
		const timeoutMs = config.baseMs;

		timer = setTimeout(() => {
			stallCount += 1;

			config.sendAnalyticsEvent({
				eventType: 'track',
				action: 'timeout',
				actionSubject: 'rovoChatStream',
				attributes: {
					stallCount,
					stage,
					timeoutMs,
				},
			});
			// Keep armed so we continue to detect subsequent stalls until chunks resume
			schedule('betweenChunks');
		}, timeoutMs) as unknown as number;
	};

	return {
		arm: (): void => {
			if (armed) {
				return;
			}
			armed = true;
			// Start pre-first-chunk watchdog waiting for the first stream event
			schedule('newResponse');
		},
		onChunk: (): void => {
			if (!armed) {
				return;
			}
			if (firstChunkArrived === false) {
				firstChunkArrived = true;
				// After the first chunk, schedule timeout waiting for the next event
				schedule('firstGap');
				return;
			}
			// For subsequent chunks, re-arm the between-chunks watchdog
			schedule('betweenChunks');
		},
		stop: (): void => {
			clearTimer();
			armed = false;
		},
	};
};
